import { useState, useEffect, useMemo } from "react";

const LOGO_SRC = "/logo.png";

const MEMBER_DB = {
  "admin@yoursonly.com": { name: "Admin", role: "admin", cap: 20, password: "admin123" },
};

const DESK_GROUPS = [
  { id:"top",  desks:[{id:"1",monitor:false},{id:"2",monitor:true},{id:"3",monitor:true},{id:"4",monitor:false}] },
  { id:"left", desks:[{id:"5",monitor:true},{id:"7",monitor:false},{id:"9",monitor:false},{id:"6",monitor:false},{id:"8",monitor:false},{id:"10",monitor:true}] },
  { id:"mid",  desks:[{id:"11",monitor:false},{id:"13",monitor:false},{id:"15",monitor:false},{id:"12",monitor:true},{id:"14",monitor:true},{id:"16",monitor:true}] },
  { id:"solo", desks:[{id:"17",monitor:false}] },
];
const ALL_DESKS = DESK_GROUPS.flatMap(g=>g.desks);

const ROOMS = [
  { id:"breakout",  name:"Breakout Room",              icon:"🛋" },
  { id:"content",   name:"Content Studio / Meeting Rm", icon:"🎬" },
  { id:"kitchen",   name:"Kitchen Table",               icon:"☕" },
  { id:"stairs",    name:"Under Stairs",                icon:"🪜" },
];

// Generate 30-min time slots 8am–8pm
const TIME_SLOTS = [];
for(let h=8;h<20;h++){
  TIME_SLOTS.push(`${String(h).padStart(2,"0")}:00`);
  TIME_SLOTS.push(`${String(h).padStart(2,"0")}:30`);
}
// Add 20:00 as end-time only
TIME_SLOTS.push("20:00");

function fmtTime(t){ const [h,m]=t.split(":"); const hr=parseInt(h); return `${hr>12?hr-12:hr===0?12:hr}:${m}${hr>=12?"pm":"am"}`; }
const DESK_TOTAL = ALL_DESKS.length; // 17

const pad = n => String(n).padStart(2,"0");
const fmtDate = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const today = () => fmtDate(new Date());

// ── 7 DAYS/WEEK: Returns ALL days (including weekends) from start of month through Dec 31
function getAllDaysThisYear() {
  const days = [];
  const start = new Date();
  const end = new Date(start.getFullYear(), 11, 31);
  const d = new Date(start.getFullYear(), start.getMonth(), 1);
  while (d <= end) {
    days.push(fmtDate(new Date(d)));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

// ── 7 DAYS/WEEK: Returns ALL days in a given month (no weekend filter)
function getMonthDays(year, month) {
  const days=[]; const d=new Date(year,month,1);
  while(d.getMonth()===month){ days.push(fmtDate(new Date(d))); d.setDate(d.getDate()+1); }
  return days;
}

function daysInMonthBooked(bookings,email,ym){ return Object.entries(bookings).filter(([date,slots])=>date.startsWith(ym)&&slots[email]).length; }
function recurringDaysInMonth(recurring,email,ym){
  const [yr,mn]=ym.split("-").map(Number);
  const dim=new Date(yr,mn,0).getDate();
  let count=0;
  for(const rec of recurring){
    if(rec.email!==email) continue;
    const days=Array.isArray(rec.days_of_week)?rec.days_of_week:JSON.parse(rec.days_of_week||"[]");
    const start=rec.start_date||`${ym}-01`;
    const end=rec.end_date||`${ym}-${String(dim).padStart(2,"0")}`;
    for(let d=1;d<=dim;d++){
      const ds=`${ym}-${String(d).padStart(2,"0")}`;
      if(ds<start||ds>end) continue;
      const dow=new Date(ds+"T12:00:00").getDay();
      if(days.includes(dow)) count++;
    }
  }
  return count;
}

const MONTH_NAMES=["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH_SHORT=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
// 7 days/week labels
const DAY_LABELS=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// ─── Supabase client ─────────────────────────────────────────────────────────
const SUPA_URL = "https://tcdvnumestqkrzjlomwc.supabase.co";
const SUPA_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRjZHZudW1lc3Rxa3J6amxvbXdjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQyNTYyNzQsImV4cCI6MjA4OTgzMjI3NH0.QkX2ffHCePUz_2qQhfTLGPK-69ZU2QZaWg7HYRLQDak";

const supa = {
  async from(table) {
    const base = `${SUPA_URL}/rest/v1/${table}`;
    const headers = { "apikey": SUPA_KEY, "Authorization": `Bearer ${SUPA_KEY}`, "Content-Type": "application/json", "Prefer": "return=representation" };
    return {
      async select(cols="*", filter="") {
        const url = filter ? `${base}?select=${cols}&${filter}` : `${base}?select=${cols}`;
        const r = await fetch(url, { headers:{...headers, "Range-Unit":"items", "Range":"0-9999"} });
        return r.ok ? r.json() : [];
      },
      async upsert(data) {
        const r = await fetch(base, { method:"POST", headers:{...headers,"Prefer":"resolution=merge-duplicates,return=representation"}, body:JSON.stringify(data) });
        return r.ok;
      },
      async delete(match) {
        const q = Object.entries(match).map(([k,v])=>`${k}=eq.${v}`).join("&");
        const r = await fetch(`${base}?${q}`, { method:"DELETE", headers });
        return r.ok;
      },
      async update(data, match) {
        const q = Object.entries(match).map(([k,v])=>`${k}=eq.${v}`).join("&");
        const r = await fetch(`${base}?${q}`, { method:"PATCH", headers, body:JSON.stringify(data) });
        return r.ok;
      }
    };
  }
};

// ─── DB helpers ───────────────────────────────────────────────────────────────
async function loadMembers() {
  try {
    const db = await supa.from("members");
    const rows = await db.select();
    console.log("loadBookings rows:", (rows||[]).length);
    if (!rows || rows.length === 0) {
      await saveMembers(MEMBER_DB);
      return MEMBER_DB;
    }
    return Object.fromEntries(rows.map(r => [r.email, { name:r.name, role:r.role, cap:r.cap, password:r.password }]));
  } catch { return MEMBER_DB; }
}

async function saveMembers(membersObj) {
  try {
    const db = await supa.from("members");
    const rows = Object.entries(membersObj).map(([email, m]) => ({ email, name:m.name, role:m.role, cap:m.cap, password:m.password }));
    await db.upsert(rows);
  } catch(e) { console.error("saveMembers", e); }
}

async function loadBookings() {
  try {
    const SUPA_URL = "https://tcdvnumestqkrzjlomwc.supabase.co";
    const SUPA_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRjZHZudW1lc3Rxa3J6amxvbXdjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQyNTYyNzQsImV4cCI6MjA4OTgzMjI3NH0.QkX2ffHCePUz_2qQhfTLGPK-69ZU2QZaWg7HYRLQDak";
    const base = SUPA_URL + "/rest/v1/bookings";
    const headers = {"apikey": SUPA_KEY, "Authorization": "Bearer " + SUPA_KEY};
    let allRows = [];
    let offset = 0;
    const pageSize = 1000;
    while(true) {
      const r = await fetch(base + "?select=*&offset=" + offset + "&limit=" + pageSize, {headers});
      const rows = r.ok ? await r.json() : [];
      allRows = allRows.concat(rows);
      console.log("loadBookings page offset=" + offset + " got=" + rows.length);
      if(rows.length < pageSize) break;
      offset += pageSize;
    }
    const result = {};
    for (const r of allRows) {
      if (!result[r.date]) result[r.date] = {};
      result[r.date][r.email] = r.desk;
    }
    console.log("loadBookings total:", allRows.length, "dates:", Object.keys(result).length);
    return result;
  } catch(e) { console.error("loadBookings error:", e); return {}; }
}

// ── ROOM BOOKINGS ────────────────────────────────────────────────────────────
async function loadRecurringRooms() {
  try {
    const db = await supa.from("recurring_room_bookings");
    const rows = await db.select();
    return (rows||[]).map(r=>({...r, days_of_week: Array.isArray(r.days_of_week)?r.days_of_week:JSON.parse(r.days_of_week||"[]")}));
  } catch(e) { console.error("loadRecurringRooms",e); return []; }
}
async function saveRecurringRoom(rec) {
  try {
    const db = await supa.from("recurring_room_bookings");
    await db.upsert(rec);
  } catch(e) { console.error("saveRecurringRoom",e); }
}
async function deleteRecurringRoom(id) {
  try {
    const db = await supa.from("recurring_room_bookings");
    await db.delete("id", id);
  } catch(e) { console.error("deleteRecurringRoom",e); }
}

async function loadRoomBookings() {
  try {
    const db = await supa.from("room_bookings");
    return await db.select() || [];
  } catch { return []; }
}
async function addRoomBooking(booking) {
  try {
    const db = await supa.from("room_bookings");
    await db.upsert(booking);
  } catch(e) { console.error("addRoomBooking", e); }
}
async function removeRoomBooking(id) {
  try {
    const db = await supa.from("room_bookings");
    await db.delete({ id });
  } catch(e) { console.error("removeRoomBooking", e); }
}

async function addBooking(date, email, desk) {
  try {
    const db = await supa.from("bookings");
    await db.upsert({ date, email, desk });
  } catch(e) { console.error("addBooking", e); }
}

async function removeBooking(date, email) {
  try {
    const db = await supa.from("bookings");
    await db.delete({ date, email });
  } catch(e) { console.error("removeBooking", e); }
}

// ── RECURRING BOOKINGS ──────────────────────────────────────────────────────
async function loadRecurring() {
  try {
    const db = await supa.from("recurring_bookings");
    return await db.select() || [];
  } catch { return []; }
}

async function saveRecurring(rec) {
  try {
    const db = await supa.from("recurring_bookings");
    await db.upsert(rec);
  } catch(e) { console.error("saveRecurring", e); }
}

async function deleteRecurring(id) {
  try {
    const db = await supa.from("recurring_bookings");
    await db.delete({ id });
  } catch(e) { console.error("deleteRecurring", e); }
}

// ── DESK ASSIGNMENTS (block/assign) ─────────────────────────────────────────
async function loadDeskAssignments() {
  try {
    const db = await supa.from("desk_assignments");
    return await db.select() || [];
  } catch { return []; }
}

async function saveDeskAssignment(assignment) {
  try {
    const db = await supa.from("desk_assignments");
    await db.upsert(assignment);
  } catch(e) { console.error("saveDeskAssignment", e); }
}

async function deleteDeskAssignment(id) {
  try {
    const db = await supa.from("desk_assignments");
    await db.delete({ id });
  } catch(e) { console.error("deleteDeskAssignment", e); }
}

// Apply recurring bookings: in-memory only, no Supabase writes
async function loadCancelledBookings() {
  try {
    const db = await supa.from("cancelled_bookings");
    const rows = await db.select();
    return (rows||[]).map(r=>r.email+"::"+r.date);
  } catch(e) { return []; }
}
async function saveCancelledBooking(email, date) {
  try {
    const db = await supa.from("cancelled_bookings");
    await db.upsert({id: email+"::"+date, email, date});
  } catch(e) { console.error("saveCancelledBooking",e); }
}

async function applyRecurringBookings(recurring, existingBookings, exceptions=[]) {
  const updated = { ...existingBookings };
  const todayStr = today();
  const lookAhead = new Date();
  lookAhead.setMonth(lookAhead.getMonth() + 5);
  const lookAheadStr = fmtDate(lookAhead);

  for (const rec of recurring) {
    if (!rec.days_of_week || !rec.email || !rec.desk) continue;
    const daysArr = Array.isArray(rec.days_of_week) ? rec.days_of_week : JSON.parse(rec.days_of_week || "[]");
    if (!daysArr.length) continue;
    const start = rec.start_date || todayStr;
    const recEnd = rec.end_date || lookAheadStr;
    const end = recEnd < lookAheadStr ? recEnd : lookAheadStr;

    const d = new Date(start + "T12:00:00");
    const endDate = new Date(end + "T12:00:00");

    const cadence = rec.cadence || 1;
    const dowCounts = {};
    while (d <= endDate) {
      const dow = d.getDay();
      const ds = fmtDate(d);
      if (daysArr.includes(dow)) {
        if (!dowCounts[dow]) dowCounts[dow] = 0;
        dowCounts[dow]++;
        if (cadence === 1 || dowCounts[dow] % cadence === 1) {
          if (!updated[ds]) updated[ds] = {};
          if (!updated[ds][rec.email] && !exceptions.includes(rec.email+"::"+ds)) {
            updated[ds][rec.email] = rec.desk;
          }
        }
      }
      d.setDate(d.getDate() + 1);
    }
  }
  return updated;
}

const resetTokens={};

function pwdStrength(p){
  if(!p) return{score:0,label:"",color:"transparent",pct:0};
  let s=0; if(p.length>=8)s++; if(/[A-Z]/.test(p))s++; if(/[0-9]/.test(p))s++; if(/[^A-Za-z0-9]/.test(p))s++;
  return{score:s,label:["","Weak","Fair","Good","Strong"][s],color:["transparent","#ee5340","#d4831a","#5a9a70","#2d7a4f"][s],pct:s*25};
}

async function sendEmail({ to, subject, html }) {
  try {
    const r = await fetch("/.netlify/functions/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to, subject, html })
    });
    return r.ok;
  } catch(e) {
    console.error("sendEmail:", e);
    return false;
  }
}

function bookingConfirmHtml({ name, date, desk, cancelUrl }) {
  const d = new Date(date + "T12:00:00");
  const dateStr = d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <style>
    body { margin:0; padding:0; background:#f5f5f3; font-family:'DM Sans',sans-serif; }
    .wrap { max-width:560px; margin:40px auto; background:#fff; border-radius:4px; overflow:hidden; }
    .banner { background:#ee5340; padding:32px 40px; }
    .banner-logo { color:#fff; font-family:Georgia,serif; font-size:1.3rem; letter-spacing:.04em; }
    .banner-title { color:#fff; font-family:Georgia,serif; font-size:1.9rem; font-weight:600; margin:8px 0 0; }
    .body { padding:36px 40px; }
    .greeting { font-size:1rem; color:#1a1a1a; margin:0 0 24px; }
    .detail-box { background:#f5f5f3; border-radius:4px; padding:20px 24px; margin:0 0 28px; }
    .detail-row { display:flex; justify-content:space-between; padding:6px 0; font-size:.9rem; color:#1a1a1a; border-bottom:1px solid #e8e8e6; }
    .detail-row:last-child { border-bottom:none; }
    .detail-label { color:#888; }
    .cta { display:inline-block; padding:12px 28px; background:#ee5340; color:#fff; text-decoration:none; border-radius:3px; font-size:.85rem; letter-spacing:.1em; text-transform:uppercase; margin:0 0 28px; }
    .footer { padding:20px 40px; border-top:1px solid #e8e8e6; font-size:.8rem; color:#aaa; }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="banner">
      <div class="banner-logo">Yours Only Studios</div>
      <div class="banner-title">Booking confirmed</div>
    </div>
    <div class="body">
      <p class="greeting">Hi ${name}, your desk booking is confirmed.</p>
      <div class="detail-box">
        <div class="detail-row"><span class="detail-label">Date</span><span>${dateStr}</span></div>
        <div class="detail-row"><span class="detail-label">Desk</span><span>${desk}</span></div>
        <div class="detail-row"><span class="detail-label">Studio</span><span>Yours Only Studios</span></div>
      </div>
      <a class="cta" href="${cancelUrl}">Manage booking</a>
    </div>
    <div class="footer">Yours Only Studios</div>
  </div>
</body>
</html>`;
}

const css = `
  :root {
    --red:      #ee5340;
    --red-dk:   #d4412f;
    --red-lt:   #fbeeeb;
    --red-bdr:  rgb(252, 229, 226);
    --mint:     rgb(233, 247, 242);
    --mint-txt: #2d7a4f;
    --ink:      #1a1a1a;
    --grey:     #888;
    --line:     rgb(220, 215, 210);
    --bg:       #f5f5f3;
    --off:      #ededeb;
    --radius:   0px;
    --font:     'founders-grotesk', -apple-system, sans-serif;
    --font-hd:  'tiempos-headline', Georgia, serif;
  }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: var(--font); background: var(--bg); color: var(--red); min-height: 100vh; -webkit-font-smoothing: antialiased; font-size: 18px; line-height: 1.65; }
  .app { min-height: 100vh; display: flex; flex-direction: column; }

  .nav { background: var(--bg); border-bottom: 1px solid var(--line); padding: 0 2.5rem; height: 68px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; }
  .nav-logo { height: 36px; width: auto; display: block; }
  .nav-right { display: flex; align-items: center; gap: 1.5rem; }
  .nav-user { font-size: .82rem; color: var(--ink); font-weight: 500; }
  .nav-btn { font-family: var(--font); background: none; border: 1px solid var(--red-bdr); color: var(--red); padding: .42rem 1rem; font-size: .78rem; letter-spacing: .04em; cursor: pointer; transition: all .15s; }
  .nav-btn:hover { background: var(--red); color: white; border-color: var(--red); }

  .auth-wrap { flex: 1; display: flex; align-items: center; justify-content: center; background: var(--bg); padding: 3rem 2rem; }
  .auth-card { width: 100%; max-width: 440px; background: #fafaf8; border: 1.5px solid rgb(220,215,210); padding: 2.5rem; }
  .auth-logo-wrap { text-align: center; margin-bottom: 2.25rem; }
  .auth-logo { height: 46px; width: auto; }
  .auth-title { font-family: var(--font-hd); font-size: 2rem; font-weight: 400; color: var(--red); text-align: center; margin-bottom: .5rem; letter-spacing: .01em; }
  .auth-sub { text-align: center; font-size: 1rem; color: var(--ink); margin-bottom: 1.75rem; font-weight: 400; }
  .field { margin-bottom: 1rem; }
  .field label { display: block; font-size: .75rem; color: var(--ink); margin-bottom: .4rem; font-weight: 600; }
  .field input, .field select { width: 100%; padding: .65rem .8rem; border: 1px solid var(--line); font-size: 1rem; font-family: var(--font); background: var(--bg); color: var(--red); transition: border .15s; }
  .field input::placeholder { color: var(--red); opacity: .3; }
  .field input:focus, .field select:focus { outline: none; border-color: var(--red); }
  .btn-primary { width: 100%; padding: .8rem; background: var(--red-lt); color: var(--red); border: 1px solid var(--line); font-size: .82rem; letter-spacing: .12em; text-transform: uppercase; cursor: pointer; font-family: var(--font); transition: all .15s; margin-top: .25rem; }
  .btn-primary:hover { background: var(--red); color: white; border-color: var(--red); }
  .btn-primary:disabled { opacity: .4; cursor: default; }
  .auth-links { display: flex; justify-content: space-between; margin-top: 1.25rem; flex-wrap: wrap; gap: .5rem; }
  .auth-link { background: none; border: none; color: var(--red); font-size: .82rem; cursor: pointer; font-family: var(--font); padding: 0; transition: opacity .15s; text-decoration: underline; text-underline-offset: 3px; font-weight: 500; }
  .error-msg { background: var(--red-lt); color: var(--red); padding: .6rem .9rem; font-size: .82rem; margin-bottom: 1rem; border-left: 2px solid var(--red); }
  .success-msg { background: var(--mint); color: var(--mint-txt); padding: .6rem .9rem; font-size: .82rem; margin-bottom: 1rem; border-left: 2px solid var(--mint-txt); }
  .pwd-row { display: flex; align-items: center; gap: .5rem; margin-top: .35rem; }
  .pwd-bar { flex: 1; height: 2px; background: var(--line); }
  .pwd-fill { height: 100%; transition: all .25s; }
  .pwd-label { font-size: .68rem; white-space: nowrap; }

  .main { flex: 1; padding: 2.5rem; max-width: 1100px; margin: 0 auto; width: 100%; }
  .cal-wrap { border: 2.5px solid var(--ink); padding: 1.25rem; border-radius: 2px; }

  .tabs { display: flex; margin-bottom: 2.5rem; border-bottom: 1px solid var(--line); overflow-x: auto; white-space: nowrap; -webkit-overflow-scrolling: touch; }
  .tab { padding: .75rem .75rem .75rem 0; border: none; background: none; cursor: pointer; font-family: var(--font); font-size: .72rem; letter-spacing: .08em; text-transform: uppercase; color: var(--ink); font-weight: 600; border-bottom: 1.5px solid transparent; margin-bottom: -1px; transition: all .15s; margin-right: .5rem; white-space: nowrap; flex-shrink: 0; }
  .tab.active { color: var(--red); border-bottom-color: var(--red); }
  .tab:hover:not(.active) { color: var(--red); opacity: .8; }

  .status-bar { display: grid; grid-template-columns: repeat(4,1fr) 2fr; border: 2.5px solid var(--ink); margin-bottom: 2.5rem; overflow: hidden; }
  .status-item { padding: 1.25rem 1.5rem; border-right: 2.5px solid var(--ink); }
  .cap-bar-wrap { padding: 1.25rem 1.75rem; }
  .status-label { font-size: .62rem; text-transform: uppercase; letter-spacing: .14em; color: var(--ink); font-weight: 700; margin-bottom: .4rem; }
  .status-val { font-family: var(--font-hd); font-size: 1.75rem; font-weight: 400; color: var(--ink); line-height: 1; }
  .status-val.warn { color: #b07a18; }
  .status-val.bad  { color: #c0392b; }
  .cap-bar-label { display: flex; justify-content: space-between; font-size: .7rem; color: var(--ink); font-weight: 600; margin-bottom: .65rem; }
  .cap-bar { height: 2px; background: var(--line); overflow: hidden; }
  .cap-bar-fill { height: 100%; transition: width .5s; background: var(--red); }

  .year-strip { display: flex; gap: 4px; margin-bottom: 1.75rem; flex-wrap: wrap; }
  .month-pill { padding: .38rem .85rem; border: 1px solid #bbb; font-family: var(--font); font-size: .72rem; letter-spacing: .08em; text-transform: uppercase; color: var(--ink); font-weight: 700; cursor: pointer; background: none; transition: all .15s; }
  .month-pill.active { background: var(--red); color: white; border-color: var(--red); opacity: 1; }
  .month-pill:hover:not(.active) { opacity: 1; border-color: var(--ink); }
  .month-pill.past { opacity: .35; color: #888; }

  .home-split { display: grid; grid-template-columns: 1fr 380px; gap: 2rem; align-items: start; }
  .home-floor-panel { position: sticky; top: 88px; }
  .home-floor-title { font-family: var(--font-hd); font-size: 1rem; color: var(--ink); margin-bottom: .5rem; font-weight: 700; }
  .home-floor-sub { font-size: .75rem; color: var(--ink); font-weight: 600; margin-bottom: 1rem; }
  .home-floor-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; border: 1px solid #e8e8e8; margin-bottom: 1rem; background: #e8e8e8; }
  .home-floor-stat { background: #fff; padding: .85rem 1rem; }
  .home-floor-stat-label { font-size: .58rem; letter-spacing: .14em; text-transform: uppercase; color: var(--ink); font-weight: 700; margin-bottom: .25rem; }
  .home-floor-stat-val { font-family: var(--font-hd); font-size: 1.35rem; color: var(--ink); font-weight: 700; line-height: 1; }

  @media(max-width:900px){ .home-split{grid-template-columns:1fr} .home-floor-panel{position:static} }

  /* 7-day calendar grid */
  .cal-header { display: flex; align-items: baseline; gap: 1rem; margin-bottom: 1.5rem; }
  .cal-title { font-family: var(--font-hd); font-size: 1.5rem; font-weight: 700; color: var(--ink); }
  .cal-year { font-size: .82rem; color: #555; font-weight: 500; }
  .cal-grid { display: grid; grid-template-columns: repeat(7,1fr); gap: 3px; }
  .cal-day-label { text-align: center; font-size: .62rem; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; color: var(--ink); padding: .5rem 0 .8rem; }
  .cal-day-label.weekend { color: var(--red); opacity: .5; }
  .cal-cell { border: 1px solid #e0e0e0; padding: .75rem .6rem; cursor: pointer; transition: all .15s; min-height: 82px; display: flex; flex-direction: column; gap: .35rem; background: var(--bg); }
  .cal-cell:hover:not(.disabled):not(.past) { border-color: var(--red); box-shadow: 0 2px 8px rgba(238,83,64,.1); }
  .cal-cell.booked { background: var(--red-lt); border-color: var(--red); }
  .cal-cell.today-cell { border-color: var(--ink); border-width: 2px; }
  .cal-cell.disabled { opacity: .4; cursor: default; pointer-events: none; }
  .cal-cell.past { opacity: .5; cursor: default; background: #fafafa; }
  .cal-cell.full:not(.booked) { background: #f5f5f5; }
  .cal-cell.weekend-cell { background: #f9f9f7; }
  .cal-cell.blocked-cell { background: repeating-linear-gradient(45deg, #f0f0f0, #f0f0f0 4px, #e8e8e8 4px, #e8e8e8 8px); cursor: not-allowed; pointer-events: none; opacity: .6; }
  .cal-date { font-size: .88rem; font-weight: 700; color: var(--ink); }
  .cal-spots { font-size: .68rem; color: #444; font-weight: 500; }
  .cal-booked-tag { font-size: .62rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--red); }
  .cal-full-tag { font-size: .62rem; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: #666; }
  .cal-cell.preview-cell { border-color: var(--ink) !important; background: #fafafa; }
  .cal-hint { font-size:.72rem; color:var(--ink); opacity:.45; margin-top:.75rem; }

  .modal-overlay { position: fixed; inset: 0; background: rgba(238,83,64,.12); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 1rem; backdrop-filter: blur(4px); }
  .modal { background: var(--bg); border: 1px solid var(--line); padding: 2.25rem; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; }
  .modal-eyebrow { font-size: .62rem; letter-spacing: .16em; text-transform: uppercase; color: var(--ink); font-weight: 700; margin-bottom: .4rem; }
  .modal h3 { font-family: var(--font-hd); font-size: 1.6rem; font-weight: 400; color: var(--red); margin-bottom: .3rem; }
  .modal-sub { color: var(--ink); font-size: .88rem; margin-bottom: 1.5rem; font-weight: 500; }
  .modal-actions { display: flex; gap: .75rem; margin-top: 1.5rem; }
  .btn-confirm { flex:1; padding:.8rem; background:var(--red); color:white; border:1px solid var(--red); font-size:.75rem; letter-spacing:.12em; text-transform:uppercase; cursor:pointer; font-family:var(--font); transition:all .15s; }
  .btn-confirm:hover { background:var(--red-dk); }
  .btn-confirm:disabled { opacity:.3; cursor:default; }
  .btn-cancel-modal { padding:.8rem 1.5rem; background:none; border:1px solid var(--line); font-size:.75rem; letter-spacing:.1em; text-transform:uppercase; cursor:pointer; font-family:var(--font); color:var(--red); opacity:.6; transition:all .15s; }
  .btn-cancel-modal:hover { opacity:1; border-color:var(--red); }
  .btn-danger { flex:1; padding:.8rem; background:none; border:1px solid var(--red); color:var(--red); font-size:.75rem; letter-spacing:.12em; text-transform:uppercase; cursor:pointer; font-family:var(--font); transition:all .15s; opacity:.75; }
  .btn-danger:hover { background:var(--red); color:white; opacity:1; }

  .floorplan { background: var(--off); border: 1px solid var(--line); padding: 1.4rem; margin-bottom: .5rem; overflow-x: auto; }
  .fp-legend { display:flex; gap:1.25rem; margin-bottom:1.1rem; flex-wrap:wrap; }
  .fp-legend-item { display:flex; align-items:center; gap:.4rem; font-size:.68rem; color:var(--ink); font-weight:600; }
  .fp-legend-swatch { width:12px; height:12px; border:1px solid var(--line); flex-shrink:0; }
  .fp-legend-swatch.avail { background:white; }
  .fp-legend-swatch.taken { background:var(--line); }
  .fp-legend-swatch.mine  { background:var(--red-lt); border-color:var(--red); }
  .fp-legend-swatch.sel   { background:var(--red); }
  .fp-legend-swatch.blocked-swatch { background: repeating-linear-gradient(45deg, #ddd, #ddd 3px, #f0f0f0 3px, #f0f0f0 6px); }
  .fp-legend-swatch.assigned-swatch { background:#fff3cd; border-color:#f0ad4e; }
  .fp-section-lbl { font-size:.58rem; letter-spacing:.14em; text-transform:uppercase; color:var(--ink); font-weight:700; margin-bottom:.5rem; }
  .fp-bank { display:grid; grid-template-columns:repeat(3,1fr); gap:4px; }
  .fp-beanbags { display:flex; gap:.6rem; margin-bottom:.6rem; }
  .fp-beanbag { width:48px; height:48px; border-radius:50%; border:1px dashed var(--line); display:flex; align-items:center; justify-content:center; font-size:.52rem; color:var(--red); opacity:.3; text-align:center; line-height:1.2; }
  .fp-entrance { writing-mode:vertical-rl; font-size:.55rem; letter-spacing:.12em; text-transform:uppercase; color:var(--ink); font-weight:700; border:1px solid #999; padding:.5rem .22rem; align-self:center; }
  .desk-tile { background:var(--bg); border:1px solid var(--line); padding:.55rem .4rem .45rem; cursor:pointer; transition:all .15s; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.2rem; min-height:76px; user-select:none; position:relative; width:100%; }
  .desk-tile:hover:not(.taken-tile):not(.blocked-tile):not(.assigned-other-tile) { border-color:var(--red); }
  .desk-tile.sel-tile { border-color:var(--red); background:var(--red); }
  .desk-tile.sel-tile .desk-num,.desk-tile.sel-tile .desk-label,.desk-tile.sel-tile .desk-mon-icon { color:white; }
  .desk-tile.mine-tile { background:var(--red-lt); border-color:var(--red); }
  .desk-tile.taken-tile { background:#f0f0f0; border-color:#ddd; cursor:default; pointer-events:none; }
  .desk-tile.taken-tile .desk-num { opacity:.3; }
  .desk-tile.blocked-tile { background: repeating-linear-gradient(45deg, #e8e8e8, #e8e8e8 4px, #f5f5f5 4px, #f5f5f5 8px); border-color:#ccc; cursor:not-allowed; pointer-events:none; }
  .desk-tile.assigned-other-tile { background:#fff3cd; border-color:#f0ad4e; cursor:not-allowed; pointer-events:none; }
  .desk-num { font-size:.82rem; font-weight:700; color:var(--ink); line-height:1; }
  .desk-label { font-size:.52rem; font-weight:700; letter-spacing:.05em; text-transform:uppercase; color:#555; line-height:1; }
  .desk-tile.sel-tile .desk-label { color:rgba(255,255,255,.85); }
  .desk-tile.taken-tile .desk-label { opacity:.3; }
  .desk-mon-icon { font-size:.8rem; line-height:1; margin-top:auto; }
  .desk-tile.taken-tile .desk-mon-icon { opacity:.25; }
  .desk-owner-name { font-size:.58rem; font-weight:700; color:var(--ink); text-align:center; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:100%; line-height:1.2; }
  .desk-tile.mine-tile .desk-num { color:var(--red); }
  .desk-tile.mine-tile .desk-label { color:var(--red); opacity:.75; }
  .desk-tile.mine-tile .desk-owner-name { color:var(--red); font-weight:700; }
  .desk-tile.sel-tile .desk-owner-name { color:white; }
  .desk-tile.taken-tile .desk-owner-name { color:var(--ink); opacity:.8; }

  .bookings-list { border:1px solid var(--line); overflow:hidden; }
  .booking-row { padding:1.25rem 1.5rem; display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--line); flex-wrap:wrap; gap:.75rem; transition:background .12s; }
  .booking-row:last-child { border-bottom:none; }
  .booking-row:hover { background:var(--off); }
  .booking-date { font-weight:700; font-size:.9rem; color:var(--ink); }
  .booking-meta { font-size:.75rem; color:var(--ink); font-weight:500; margin-top:.15rem; }
  .booking-desk-badge { font-size:.65rem; letter-spacing:.12em; text-transform:uppercase; color:var(--red); background:var(--red-lt); padding:.3rem .8rem; border:1px solid var(--line); }
  .btn-cancel-booking { background:none; border:none; color:var(--red); font-size:.7rem; letter-spacing:.1em; text-transform:uppercase; cursor:pointer; font-family:var(--font); font-weight:600; transition:opacity .15s; }
  .empty-state { text-align:center; padding:4rem 1rem; color:var(--ink); }
  .empty-state p { font-family:var(--font-hd); font-size:1.1rem; margin-bottom:.5rem; }

  .admin-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:1px; border:1px solid var(--line); margin-bottom:2.5rem; overflow:hidden; background:var(--line); }
  .admin-stat { background:var(--bg); padding:1.5rem; }
  .admin-stat-label { font-size:.62rem; text-transform:uppercase; letter-spacing:.14em; color:var(--ink); font-weight:700; margin-bottom:.4rem; }
  .admin-stat-val { font-family:var(--font-hd); font-size:2.2rem; color:var(--ink); line-height:1; }
  .admin-stat-sub { font-size:.72rem; color:#555; font-weight:500; margin-top:.3rem; }

  .admin-section { margin-bottom:3rem; }
  .section-title { font-size:.65rem; letter-spacing:.18em; text-transform:uppercase; color:var(--ink); font-weight:700; margin-bottom:1.25rem; padding-bottom:.75rem; border-bottom:2px solid var(--ink); display:flex; align-items:center; justify-content:space-between; }

  .day-picker-row { display:flex; align-items:center; gap:.75rem; margin-bottom:1.5rem; flex-wrap:wrap; }
  .day-input { border:1px solid var(--line); padding:.55rem .8rem; font-family:var(--font); font-size:.88rem; color:var(--red); background:var(--bg); cursor:pointer; }
  .day-input:focus { outline:none; border-color:var(--red); }
  .day-view-card { border:1px solid var(--line); overflow:hidden; margin-bottom:2rem; }
  .day-view-header { background:var(--red); color:white; padding:1rem 1.5rem; display:flex; align-items:center; justify-content:space-between; }
  .day-view-title { font-family:var(--font-hd); font-size:1.1rem; font-weight:400; }
  .day-view-stats { font-size:.75rem; opacity:.8; }
  .day-view-body { display:grid; grid-template-columns:1fr 1fr; }
  .day-view-half { padding:1.5rem; border-right:1px solid var(--line); }
  .day-view-half:last-child { border-right:none; }
  .day-view-half-title { font-size:.62rem; letter-spacing:.14em; text-transform:uppercase; color:var(--ink); font-weight:700; margin-bottom:1rem; }
  .day-desk-row { display:flex; align-items:center; justify-content:space-between; padding:.55rem 0; border-bottom:1px solid var(--line); font-size:.85rem; }
  .day-desk-row:last-child { border-bottom:none; }
  .day-desk-num { font-weight:700; color:var(--ink); min-width:48px; }
  .day-desk-name { color:var(--ink); font-weight:500; }
  .day-desk-empty { color:#888; font-style:italic; font-weight:400; }
  .day-desk-monitor { font-size:.6rem; letter-spacing:.08em; text-transform:uppercase; color:var(--red); opacity:.35; background:var(--red-lt); padding:.1rem .4rem; margin-left:.4rem; }

  .member-table { width:100%; border-collapse:collapse; border:1px solid var(--line); overflow:hidden; }
  .member-table th { background:var(--off); color:var(--ink); padding:.75rem 1.25rem; text-align:left; font-size:.62rem; letter-spacing:.14em; text-transform:uppercase; font-weight:700; border-bottom:1px solid var(--line); }
  .member-table td { padding:.9rem 1.25rem; border-bottom:1px solid var(--line); font-size:.82rem; color:var(--ink); font-weight:500; }
  .member-table tr:last-child td { border-bottom:none; }
  .member-table tr:hover td { background:var(--off); }
  .cap-select { border:1px solid var(--line); padding:.35rem .6rem; font-family:var(--font); font-size:.8rem; background:var(--bg); cursor:pointer; color:var(--red); }
  .cap-select:focus { outline:none; border-color:var(--red); }
  .add-member-form { border:1px solid var(--line); padding:1.75rem; display:flex; gap:.75rem; flex-wrap:wrap; align-items:flex-end; background:var(--off); }
  .add-member-form .field { margin:0; flex:1; min-width:150px; }
  .btn-add { padding:.78rem 1.75rem; background:var(--red); color:white; border:1px solid var(--red); font-size:.7rem; letter-spacing:.15em; text-transform:uppercase; cursor:pointer; font-family:var(--font); white-space:nowrap; transition:background .15s; }
  .btn-add:hover { background:var(--red-dk); }
  .role-badge { font-size:.6rem; letter-spacing:.1em; text-transform:uppercase; font-weight:500; padding:.2rem .65rem; }
  .role-badge.admin { background:var(--red-lt); color:var(--red); border:1px solid var(--line); }
  .role-badge.member { background:var(--off); color:var(--red); opacity:.5; border:1px solid var(--line); }
  .btn-remove { background:none; border:none; color:var(--red); font-size:.7rem; letter-spacing:.08em; text-transform:uppercase; cursor:pointer; font-family:var(--font); font-weight:600; transition:opacity .15s; }
  .btn-remove:hover { opacity:.9; }
  .occupancy-bar { height:3px; background:var(--line); margin-top:.3rem; }
  .occupancy-fill { height:100%; background:var(--red); transition:width .4s; }
  .upcoming-tag { font-size:.62rem; letter-spacing:.08em; text-transform:uppercase; color:var(--red); background:var(--red-lt); padding:.18rem .55rem; border:1px solid var(--line); display:inline-block; }

  /* Recurring & desk assignment UI */
  .recur-form { border:1px solid var(--line); padding:1.5rem; background:var(--off); margin-bottom:1rem; }
  .recur-form h4 { font-size:.7rem; letter-spacing:.14em; text-transform:uppercase; color:var(--ink); font-weight:700; margin-bottom:1rem; }
  .day-check-row { display:flex; gap:.6rem; flex-wrap:wrap; margin:.5rem 0; }
  .day-check { display:flex; align-items:center; gap:.3rem; font-size:.78rem; color:var(--ink); cursor:pointer; }
  .day-check input { cursor:pointer; accent-color:var(--red); }
  .recur-list-item { display:flex; align-items:center; justify-content:space-between; padding:.75rem 1rem; border-bottom:1px solid var(--line); font-size:.82rem; flex-wrap:wrap; gap:.5rem; }
  .recur-list-item:last-child { border-bottom:none; }
  .recur-badge { font-size:.6rem; letter-spacing:.08em; text-transform:uppercase; background:var(--mint); color:var(--mint-txt); padding:.2rem .5rem; border:1px solid rgba(45,122,79,.2); }
  .assign-badge { font-size:.6rem; letter-spacing:.08em; text-transform:uppercase; background:#fff3cd; color:#856404; padding:.2rem .5rem; border:1px solid #f0ad4e; }
  .blocked-badge { font-size:.6rem; letter-spacing:.08em; text-transform:uppercase; background:#f8d7da; color:#842029; padding:.2rem .5rem; border:1px solid #f5c2c7; }

  .room-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; }
  @media(max-width:900px){ .room-grid { grid-template-columns:repeat(2,1fr) !important; } }
  @media(max-width:640px){ .room-grid { grid-template-columns:1fr !important; } }
  @media(max-width:640px){
    .main{padding:1.25rem} .nav{padding:0 1.25rem}
    .status-bar{grid-template-columns:1fr 1fr}
    .status-item{border-right:none; border-bottom:2.5px solid var(--ink);}
    .status-item:nth-child(1){border-right:2.5px solid var(--ink);}
    .status-item:nth-child(2){border-right:none;}
    .status-item:nth-child(3){border-right:2.5px solid var(--ink); border-bottom:none;}
    .status-item:nth-child(4){border-right:none; border-bottom:none;}
    .cap-bar-wrap{grid-column:1/-1; border-top:2.5px solid var(--ink); border-right:none;}
    .cal-cell{min-height:58px;padding:.5rem .3rem} .cal-grid{gap:2px} .cal-date{font-size:.78rem} .cal-spots{font-size:.58rem}
    .admin-grid{grid-template-columns:1fr 1fr}
    .day-view-body{grid-template-columns:1fr}
    .day-view-half{border-right:none;border-bottom:1px solid var(--line)}
  }
`;

const DOW_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function App() {
  const now = new Date();
  const [user,setUser]=useState(null);
  const [members,setMembers]=useState(MEMBER_DB);
  const [bookings,setBookings]=useState({});
  const [roomBookings,setRoomBookings]=useState([]);
  const [roomTab,setRoomTab]=useState("book"); // "book" | "mine"
  const [roomDate,setRoomDate]=useState(()=>today());
  const [roomModal,setRoomModal]=useState(null); // {room, date, slot}
  const [roomName,setRoomName]=useState("");
  const [roomDuration,setRoomDuration]=useState(1);
  const [tab,setTab]=useState("book");
  const [viewMonth,setViewMonth]=useState(now.getMonth());
  const [viewYear,setViewYear]=useState(now.getFullYear());
  const [modal,setModal]=useState(null);
  const [selectedDesk,setSelectedDesk]=useState(null);
  const [loaded,setLoaded]=useState(false);
  const [authScreen,setAuthScreen]=useState("login");
  const [authEmail,setAuthEmail]=useState("");
  const [authPass,setAuthPass]=useState("");
  const [authPass2,setAuthPass2]=useState("");
  const [authName,setAuthName]=useState("");
  const [authErr,setAuthErr]=useState("");
  const [authOk,setAuthOk]=useState("");
  const [resetToken,setResetToken]=useState("");
  const [newPassword,setNewPassword]=useState("");
  const [adminDay,setAdminDay]=useState(fmtDate(now));
  const [newMember,setNewMember]=useState({email:"",name:"",cap:8,password:"pass"});
  const [extraDaysEmail,setExtraDaysEmail]=useState("");
  const [extraDaysCount,setExtraDaysCount]=useState(1);
  const [extraDaysMonth,setExtraDaysMonth]=useState(()=>`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,"0")}`);
  const [extraDaysMsg,setExtraDaysMsg]=useState("");
  const [addMemberMsg,setAddMemberMsg]=useState("");

  // Recurring bookings state
  const [recurring, setRecurring] = useState([]);
  const [newRecur, setNewRecur] = useState({ email:"", desk:"1", days_of_week:[], start_date:"", end_date:"", cadence:1 });
  const [recurMsg, setRecurMsg] = useState("");

  // Desk assignments state
  const [deskAssignments, setDeskAssignments] = useState([]);
  const [newAssign, setNewAssign] = useState({ desk_id:"1", email:"", label:"", start_date:"", end_date:"", type:"assign" });
  const [assignMsg, setAssignMsg] = useState("");
  const [adminRoomEmail, setAdminRoomEmail] = useState("");
  const [adminRoomId, setAdminRoomId] = useState("breakout");
  const [adminRoomDate, setAdminRoomDate] = useState(()=>today());
  const [adminRoomMsg, setAdminRoomMsg] = useState("");
  const [manualBookEmail, setManualBookEmail] = useState("");
  const [manualBookDesk, setManualBookDesk] = useState("1");
  const [manualBookDate, setManualBookDate] = useState(()=>today());
  const [manualBookMsg, setManualBookMsg] = useState("");
  const [editBookingModal, setEditBookingModal] = useState(null); // {type:'desk'|'room', item}
  const [editDesk, setEditDesk] = useState('1');
  const [editDate, setEditDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [recurRoom, setRecurRoom] = useState({email:"", room_id:"breakout", days_of_week:[], start_date:"", end_date:"", label:"", cadence:1});
  const [recurringRooms, setRecurringRooms] = useState([]);
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [recurRoomMsg, setRecurRoomMsg] = useState("");
  const [openWeeks, setOpenWeeks] = useState({});
  const [openMyWeeks, setOpenMyWeeks] = useState({});
  const toggleMyWeek = (key) => setOpenMyWeeks(w => ({...w, [key]: !w[key]}));
  const [calView, setCalView] = useState("month"); // "day" | "week" | "month"
  const [calDate, setCalDate] = useState(()=>today());
  const toggleWeek = (key) => setOpenWeeks(w => ({...w, [key]: !w[key]}));

  useEffect(()=>{(async()=>{
    const[b,m,rec,asgn,rb,rrec,cancelled]=await Promise.all([
      loadBookings(),loadMembers(),loadRecurring(),loadDeskAssignments(),loadRoomBookings(),loadRecurringRooms(),loadCancelledBookings()
    ]);
    // Apply recurring bookings on top of existing bookings
    const recList = (rec||[]).map(r=>({...r, days_of_week: Array.isArray(r.days_of_week)?r.days_of_week:JSON.parse(r.days_of_week||"[]")}));
    const bWithRecurring = recList.length > 0 ? await applyRecurringBookings(recList, b, cancelled||[]) : b;
    setBookings(bWithRecurring);setMembers(m);setRecurring(recList);setDeskAssignments(asgn||[]);setRoomBookings(rb||[]);setRecurringRooms(rrec||[]);setCancelledBookings(cancelled||[]);
    // Restore session after members are loaded
    try {
      const saved = localStorage.getItem("yos_session");
      if(saved) {
        const u = JSON.parse(saved);
        if(m[u.email]) {
          // Refresh user data from latest members record
          setUser({...u,...m[u.email],email:u.email});
        } else {
          localStorage.removeItem("yos_session");
        }
      }
    } catch {}
    setLoaded(true);
  })();}, []);

  const reloadBookings = async () => {
    const b = await loadBookings();
    const recList = recurring.map(r=>({...r, days_of_week: Array.isArray(r.days_of_week)?r.days_of_week:JSON.parse(r.days_of_week||"[]")}));
    const bWithRecurring = recList.length > 0 ? await applyRecurringBookings(recList, b, cancelledBookings) : b;
    setBookings(bWithRecurring);
  };

  const persistMembers=async(m)=>{
    await saveMembers(m);
    // Keep session fresh if current user data changed
    if(user && m[user.email]){
      const updated={...user,...m[user.email],email:user.email};
      setUser(updated);
      localStorage.setItem("yos_session",JSON.stringify(updated));
    }
  };

  // ── AUTH ──
  const handleLogin=()=>{
    setAuthErr("");
    const m=members[authEmail.trim().toLowerCase()];
    if(!m||m.password!==authPass){setAuthErr("Incorrect email or password.");return;}
    setUser({email:authEmail.trim().toLowerCase(),...m});
  };
  const handleSignup=()=>{
    setAuthErr("");
    const email=authEmail.trim().toLowerCase();
    if(!authName.trim()){setAuthErr("Please enter your name.");return;}
    if(!email.includes("@")){setAuthErr("Please enter a valid email.");return;}
    if(members[email]){setAuthErr("An account with this email already exists.");return;}
    if(authPass.length<6){setAuthErr("Password must be at least 6 characters.");return;}
    if(authPass!==authPass2){setAuthErr("Passwords don't match.");return;}
    const updated={...members,[email]:{name:authName.trim(),role:"member",cap:8,password:authPass}};
    setMembers(updated);persistMembers(updated);
    setUser({email,name:authName.trim(),role:"member",cap:8,password:authPass});
  };
  const handleForgot=()=>{
    setAuthErr("");setAuthOk("");
    const email=authEmail.trim().toLowerCase();
    if(!members[email]){setAuthErr("No account found with that email.");return;}
    const token=Math.random().toString(36).slice(2,8).toUpperCase();
    resetTokens[email]=token;
    setAuthOk(`Reset code: ${token}  (in production this is emailed to you)`);
  };
  const handleReset=()=>{
    setAuthErr("");setAuthOk("");
    const email=authEmail.trim().toLowerCase();
    if(resetTokens[email]!==resetToken.trim().toUpperCase()){setAuthErr("Invalid reset code.");return;}
    if(newPassword.length<6){setAuthErr("Password must be at least 6 characters.");return;}
    const updated={...members,[email]:{...members[email],password:newPassword}};
    setMembers(updated);persistMembers(updated);
    delete resetTokens[email];
    setAuthOk("Password updated! You can now sign in.");
    setAuthScreen("login");setAuthPass("");setNewPassword("");setResetToken("");
  };

  // ── BOOKING ──
  const todayStr=today();
  const [previewDate, setPreviewDate] = useState(()=>today());
  const ym=`${viewYear}-${pad(viewMonth+1)}`;
  const workdays=useMemo(()=>getMonthDays(viewYear,viewMonth),[viewYear,viewMonth]);
  const usedThisMonth=user?daysInMonthBooked(bookings,user.email,ym):0;
  const extraBonus=user?Number(localStorage.getItem(`extra_${user.email}_${ym}`)||0):0;
  const capThisMonth=user?((members[user.email]?.cap??user.cap)+extraBonus):0;
  const remaining=capThisMonth-usedThisMonth;
  const slotsFor=date=>bookings[date]||{};
  const countBooked=date=>Object.keys(slotsFor(date)).length;
  const userHasDay=date=>user&&!!slotsFor(date)[user.email];

  // Get desk assignment info for a given date and desk
  const getDeskStatus = (deskId, date) => {
    for (const a of deskAssignments) {
      if (a.desk_id !== deskId) continue;
      const start = a.start_date || "0000-01-01";
      const end = a.end_date || "9999-12-31";
      if (date >= start && date <= end) {
        return { type: a.email ? "assigned" : "blocked", email: a.email, label: a.label };
      }
    }
    return null;
  };

  // Check if a date has any fully blocked desks (for calendar display)
  const getDateDeskInfo = (date) => {
    const blocked = deskAssignments.filter(a => {
      if (a.email) return false; // assigned, not blocked
      const start = a.start_date || "0000-01-01";
      const end = a.end_date || "9999-12-31";
      return date >= start && date <= end;
    });
    return blocked;
  };

  const myBookings=useMemo(()=>{
    if(!user) return[];
    return Object.entries(bookings).filter(([date,slots])=>slots[user.email]&&date>=todayStr).sort(([a],[b])=>a.localeCompare(b)).map(([date,slots])=>({date,desk:slots[user.email]}));
  },[bookings,user]);

  const openModal=date=>{
    if(!user) return;
    // Non-admin: enforce 60-day advance booking limit
    if(!isAdmin){
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth()+5);
      if(new Date(date+"T12:00:00") > maxDate) return;
    }
    const used=daysInMonthBooked(bookings,user.email,date.slice(0,7));
    const cap=members[user.email]?.cap??user.cap;
    if(used>=cap&&!userHasDay(date)) return;
    setSelectedDesk(null);setModal({date});
  };

  const confirmBooking=async()=>{
    if(!selectedDesk||!modal) return;
    await addBooking(modal.date, user.email, selectedDesk);
    const updated={...bookings,[modal.date]:{...(bookings[modal.date]||{}),[user.email]:selectedDesk}};
    setBookings(updated);setModal(null);
    const cancelUrl = `https://book.yoursonlystudios.com`;
    sendEmail({
      to: user.email,
      subject: `Booking confirmed – ${modal.date}`,
      html: bookingConfirmHtml({ name: user.name, date: modal.date, desk: `Desk ${selectedDesk}`, cancelUrl })
    });
  };

  const cancelBooking=async date=>{
    const desk = bookings[date]?.[user.email];
    await removeBooking(date, user.email);
    // Save exception so recurring booking doesnt regenerate
    await saveCancelledBooking(user.email, date);
    setCancelledBookings(cb=>[...cb, user.email+"::"+date]);
    const updated={...bookings};
    if(updated[date]){const day={...updated[date]};delete day[user.email];if(!Object.keys(day).length)delete updated[date];else updated[date]=day;}
    setBookings(updated);setModal(null);
    if(desk){
      const d=new Date(date+"T12:00:00");
      const dateStr=d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
      sendEmail({
        to: user.email,
        subject: `Booking cancelled – ${date}`,
        html: `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>body{margin:0;padding:0;background:#f5f5f3;font-family:'DM Sans',sans-serif;}.wrap{max-width:560px;margin:40px auto;background:#fff;border-radius:4px;overflow:hidden;}.banner{background:#1a1a1a;padding:32px 40px;}.banner-logo{color:#fff;font-family:Georgia,serif;font-size:1.3rem;letter-spacing:.04em;}.banner-title{color:#ee5340;font-family:Georgia,serif;font-size:1.9rem;font-weight:600;margin:8px 0 0;}.body{padding:36px 40px;}.greeting{font-size:1rem;color:#1a1a1a;margin:0 0 24px;}.detail-box{background:#f5f5f3;border-radius:4px;padding:20px 24px;margin:0 0 28px;}.detail-row{display:flex;justify-content:space-between;padding:6px 0;font-size:.9rem;color:#1a1a1a;border-bottom:1px solid #e8e8e6;}.detail-row:last-child{border-bottom:none;}.detail-label{color:#888;}.cta{display:inline-block;padding:12px 28px;background:#1a1a1a;color:#fff;text-decoration:none;border-radius:3px;font-size:.85rem;letter-spacing:.1em;text-transform:uppercase;margin:0 0 28px;}.footer{padding:20px 40px;border-top:1px solid #e8e8e6;font-size:.8rem;color:#aaa;}</style></head><body><div class="wrap"><div class="banner"><div class="banner-logo">Yours Only Studios</div><div class="banner-title">Booking cancelled</div></div><div class="body"><p class="greeting">Hi ${user.name}, your booking has been cancelled.</p><div class="detail-box"><div class="detail-row"><span class="detail-label">Date</span><span>${dateStr}</span></div><div class="detail-row"><span class="detail-label">Desk</span><span>Desk ${desk}</span></div></div><a class="cta" href="https://book.yoursonlystudios.com">Book another desk</a></div><div class="footer">Yours Only Studios</div></div></body></html>`
      });
    }
  };

  // ── ADMIN helpers ──
  const updateCap=(email,cap)=>{
    const updated={...members,[email]:{...members[email],cap:Number(cap)}};
    setMembers(updated);persistMembers(updated);
    if(user?.email===email) setUser(u=>({...u,cap:Number(cap)}));
  };
  const addMember=async()=>{
    const email=newMember.email.trim().toLowerCase();
    if(!email||!newMember.name.trim()) return;
    const pwd=newMember.password||"pass";
    const name=newMember.name.trim();
    const updated={...members,[email]:{name,role:"member",cap:newMember.cap,password:pwd}};
    setMembers(updated);persistMembers(updated);
    // Send welcome email directly via Resend
    const welcomeHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>body{margin:0;padding:0;background:#f5f5f3;font-family:'DM Sans',sans-serif;}.wrap{max-width:560px;margin:40px auto;background:#fff;border-radius:4px;overflow:hidden;}.banner{background:#ee5340;padding:32px 40px;}.banner-logo{color:#fff;font-family:Georgia,serif;font-size:1.3rem;letter-spacing:.04em;}.banner-title{color:#fff;font-family:Georgia,serif;font-size:1.9rem;font-weight:600;margin:8px 0 0;}.body{padding:36px 40px;}.greeting{font-size:1rem;color:#1a1a1a;margin:0 0 24px;}.detail-box{background:#f5f5f3;border-radius:4px;padding:20px 24px;margin:0 0 28px;}.detail-row{display:flex;justify-content:space-between;padding:6px 0;font-size:.9rem;color:#1a1a1a;border-bottom:1px solid #e8e8e6;}.detail-row:last-child{border-bottom:none;}.detail-label{color:#888;}.cta{display:inline-block;padding:12px 28px;background:#ee5340;color:#fff;text-decoration:none;border-radius:3px;font-size:.85rem;letter-spacing:.1em;text-transform:uppercase;margin:0 0 28px;}.footer{padding:20px 40px;border-top:1px solid #e8e8e6;font-size:.8rem;color:#aaa;}</style></head><body><div class="wrap"><div class="banner"><div class="banner-logo">Yours Only Studios</div><div class="banner-title">Welcome to the studio</div></div><div class="body"><p class="greeting">Hi ${name}, your desk booking account is ready.</p><div class="detail-box"><div class="detail-row"><span class="detail-label">Login URL</span><span>book.yoursonlystudios.com</span></div><div class="detail-row"><span class="detail-label">Email</span><span>${email}</span></div><div class="detail-row"><span class="detail-label">Password</span><span>${pwd}</span></div><div class="detail-row"><span class="detail-label">Days per month</span><span>${newMember.cap} days</span></div></div><a class="cta" href="https://book.yoursonlystudios.com">Log in now</a><p style="font-size:.82rem;color:#888;">We recommend changing your password after your first login.</p></div><div class="footer">Yours Only Studios</div></div></body></html>`;
    const ok = await sendEmail({
      to: email,
      subject: "Welcome to Yours Only Studios — your booking account",
      html: welcomeHtml
    });
    setNewMember({email:"",name:"",cap:8,password:"pass"});
    if(ok) {
      setAddMemberMsg(`✓ ${name} added and welcome email sent to ${email}`);
    } else {
      setAddMemberMsg(`✓ ${name} added — but email failed, check Resend logs`);
    }
    setTimeout(()=>setAddMemberMsg(""),5000);
  };
  const grantExtraDays=()=>{
    const email=extraDaysEmail.trim().toLowerCase();
    if(!email||!members[email]){setExtraDaysMsg("Member not found.");return;}
    const key=`extra_${email}_${extraDaysMonth}`;
    const current=Number(localStorage.getItem(key)||0);
    localStorage.setItem(key,current+extraDaysCount);
    setExtraDaysMsg(`✓ Added ${extraDaysCount} extra day${extraDaysCount>1?"s":""} to ${members[email].name} for ${extraDaysMonth}`);
    setTimeout(()=>setExtraDaysMsg(""),4000);
  };
  const getExtraDays=(email,ym)=>Number(localStorage.getItem(`extra_${email}_${ym}`)||0);
  const removeMember=async email=>{
    if(email===user.email)return;
    const u={...members};delete u[email];setMembers(u);persistMembers(u);
    // Also delete from Supabase directly
    try{ const db=await supa.from("members"); await db.delete({email}); }catch(e){console.error("removeMember",e);}
  };

  // ── RECURRING BOOKINGS ──
  const addRecurring = async () => {
    if (!newRecur.email || !newRecur.desk || !newRecur.days_of_week.length) {
      setRecurMsg("Please fill in member, desk, and at least one day."); setTimeout(()=>setRecurMsg(""),3000); return;
    }
    if (!members[newRecur.email]) { setRecurMsg("Member not found."); setTimeout(()=>setRecurMsg(""),3000); return; }
    const rec = {
      id: Date.now().toString(),
      email: newRecur.email,
      desk: newRecur.desk,
      days_of_week: JSON.stringify(newRecur.days_of_week),
      start_date: newRecur.start_date || todayStr,
      end_date: newRecur.end_date || `${now.getFullYear()}-12-31`,
      cadence: newRecur.cadence || 1,
    };
    await saveRecurring(rec);
    const newList = [...recurring, { ...rec, days_of_week: newRecur.days_of_week }];
    setRecurring(newList);
    // Apply the recurring booking to existing bookings
    const updatedBookings = await applyRecurringBookings([{ ...rec, days_of_week: newRecur.days_of_week }], bookings);
    setBookings(updatedBookings);
    // Capture values BEFORE resetting form
    const recurEmail = newRecur.email;
    const recurDesk = newRecur.desk;
    const recurDays = [...newRecur.days_of_week];
    const recurStart = newRecur.start_date || rec.start_date;
    const recurEnd = newRecur.end_date || rec.end_date;
    setNewRecur({ email:"", desk:"1", days_of_week:[], start_date:"", end_date:"", cadence:1 });
    setRecurMsg("✓ Recurring booking created and applied.");
    setTimeout(()=>setRecurMsg(""),4000);
    // Send confirmation email to the member
    const memberName = members[recurEmail]?.name || recurEmail;
    const daysText = recurDays.map(d=>["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]).join(", ");
    sendEmail({
      to: recurEmail,
      subject: `Recurring desk booking set up – Desk ${newRecur.desk}`,
      html: `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>body{margin:0;padding:0;background:#f5f5f3;font-family:'DM Sans',sans-serif;}.wrap{max-width:560px;margin:40px auto;background:#fff;border-radius:4px;overflow:hidden;}.banner{background:#ee5340;padding:32px 40px;}.banner-logo{color:#fff;font-family:Georgia,serif;font-size:1.3rem;}.banner-title{color:#fff;font-family:Georgia,serif;font-size:1.9rem;font-weight:600;margin:8px 0 0;}.body{padding:36px 40px;}.greeting{font-size:1rem;color:#1a1a1a;margin:0 0 24px;}.detail-box{background:#f5f5f3;border-radius:4px;padding:20px 24px;margin:0 0 28px;}.detail-row{display:flex;justify-content:space-between;padding:6px 0;font-size:.9rem;color:#1a1a1a;border-bottom:1px solid #e8e8e6;}.detail-row:last-child{border-bottom:none;}.detail-label{color:#888;}.footer{padding:20px 40px;border-top:1px solid #e8e8e6;font-size:.8rem;color:#aaa;}</style></head><body><div class="wrap"><div class="banner"><div class="banner-logo">Yours Only Studios</div><div class="banner-title">Recurring booking confirmed</div></div><div class="body"><p class="greeting">Hi ${memberName}, a recurring desk booking has been set up for you.</p><div class="detail-box"><div class="detail-row"><span class="detail-label">Desk</span><span>Desk ${recurDesk}</span></div><div class="detail-row"><span class="detail-label">Days</span><span>${daysText}</span></div><div class="detail-row"><span class="detail-label">From</span><span>${recurStart}</span></div><div class="detail-row"><span class="detail-label">Until</span><span>${recurEnd}</span></div></div></div><div class="footer">Yours Only Studios</div></div></body></html>`
    });
  };

  const removeRecurring = async (rec) => {
    // Delete the rule
    await deleteRecurring(rec.id);
    setRecurring(r => r.filter(x => x.id !== rec.id));
    // Also delete all future generated bookings for this recurring rule
    const daysArr = Array.isArray(rec.days_of_week) ? rec.days_of_week : JSON.parse(rec.days_of_week||"[]");
    const todayD = todayStr;
    const end = rec.end_date || `${new Date().getFullYear()}-12-31`;
    const toDelete = Object.entries(bookings).filter(([date, slots]) => {
      if(date < todayD || date > end) return false;
      if(!slots[rec.email] || slots[rec.email] !== rec.desk) return false;
      const dow = new Date(date+"T12:00:00").getDay();
      return daysArr.includes(dow);
    });
    // Remove from state and Supabase
    const updatedBookings = {...bookings};
    for(const [date] of toDelete) {
      await removeBooking(date, rec.email);
      if(updatedBookings[date]) {
        const day = {...updatedBookings[date]};
        delete day[rec.email];
        if(!Object.keys(day).length) delete updatedBookings[date];
        else updatedBookings[date] = day;
      }
    }
    setBookings(updatedBookings);
  };

  const toggleRecurDay = (dow) => {
    setNewRecur(r => ({
      ...r,
      days_of_week: r.days_of_week.includes(dow)
        ? r.days_of_week.filter(d => d !== dow)
        : [...r.days_of_week, dow]
    }));
  };

  // ── DESK ASSIGNMENTS ──
  const addDeskAssignment = async () => {
    if (!newAssign.desk_id) { setAssignMsg("Please select a desk."); setTimeout(()=>setAssignMsg(""),3000); return; }
    if (newAssign.type === "assign" && !newAssign.email) { setAssignMsg("Please enter a member email."); setTimeout(()=>setAssignMsg(""),3000); return; }
    const a = {
      id: Date.now().toString(),
      desk_id: newAssign.desk_id,
      email: newAssign.type === "assign" ? newAssign.email : null,
      label: newAssign.label || (newAssign.type === "blocked" ? "Blocked" : "Assigned"),
      start_date: newAssign.start_date || todayStr,
      end_date: newAssign.end_date || `${now.getFullYear()}-12-31`,
    };
    await saveDeskAssignment(a);
    setDeskAssignments(d => [...d, a]);
    setNewAssign({ desk_id:"1", email:"", label:"", start_date:"", end_date:"", type:"assign" });
    setAssignMsg(`✓ Desk ${a.desk_id} ${a.email ? "assigned" : "blocked"} successfully.`);
    setTimeout(()=>setAssignMsg(""),4000);
    // Send email if assigning to a member (not blocking)
    if (a.email && members[a.email]) {
      const assignName = members[a.email].name || a.email;
      const d1 = new Date(a.start_date+"T12:00:00").toLocaleDateString("en-AU",{day:"numeric",month:"long",year:"numeric"});
      const d2 = new Date(a.end_date+"T12:00:00").toLocaleDateString("en-AU",{day:"numeric",month:"long",year:"numeric"});
      sendEmail({
        to: a.email,
        subject: `Desk ${a.desk_id} assigned to you – Yours Only Studios`,
        html: `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>body{margin:0;padding:0;background:#f5f5f3;font-family:'DM Sans',sans-serif;}.wrap{max-width:560px;margin:40px auto;background:#fff;border-radius:4px;overflow:hidden;}.banner{background:#ee5340;padding:32px 40px;}.banner-logo{color:#fff;font-family:Georgia,serif;font-size:1.3rem;}.banner-title{color:#fff;font-family:Georgia,serif;font-size:1.9rem;font-weight:600;margin:8px 0 0;}.body{padding:36px 40px;}.greeting{font-size:1rem;color:#1a1a1a;margin:0 0 24px;}.detail-box{background:#f5f5f3;border-radius:4px;padding:20px 24px;margin:0 0 28px;}.detail-row{display:flex;justify-content:space-between;padding:6px 0;font-size:.9rem;color:#1a1a1a;border-bottom:1px solid #e8e8e6;}.detail-row:last-child{border-bottom:none;}.detail-label{color:#888;}.cta{display:inline-block;padding:12px 28px;background:#ee5340;color:#fff;text-decoration:none;border-radius:3px;font-size:.85rem;letter-spacing:.1em;text-transform:uppercase;margin:0 0 28px;}.footer{padding:20px 40px;border-top:1px solid #e8e8e6;font-size:.8rem;color:#aaa;}</style></head><body><div class="wrap"><div class="banner"><div class="banner-logo">Yours Only Studios</div><div class="banner-title">Desk assigned to you</div></div><div class="body"><p class="greeting">Hi ${assignName}, a desk has been reserved for you.</p><div class="detail-box"><div class="detail-row"><span class="detail-label">Desk</span><span>Desk ${a.desk_id}</span></div><div class="detail-row"><span class="detail-label">From</span><span>${d1}</span></div><div class="detail-row"><span class="detail-label">Until</span><span>${d2}</span></div>${a.label?`<div class="detail-row"><span class="detail-label">Note</span><span>${a.label}</span></div>`:""}</div><a class="cta" href="https://book.yoursonlystudios.com">View bookings</a></div><div class="footer">Yours Only Studios</div></div></body></html>`
      });
    }
  };

  const removeDeskAssignment = async (id) => {
    await deleteDeskAssignment(id);
    setDeskAssignments(d => d.filter(x => x.id !== id));
  };

  // Admin dashboard stats
  const adminStats=useMemo(()=>{
    const currentYm=`${now.getFullYear()}-${pad(now.getMonth()+1)}`;
    const totalMembers=Object.values(members).filter(m=>m.role==="member").length;
    const todaySlots=slotsFor(todayStr);
    const todayBooked=Object.keys(todaySlots).length;
    const upcoming=Object.entries(bookings).filter(([date])=>date>=todayStr).sort(([a],[b])=>a.localeCompare(b));
    let busiestDay=null,busiestCount=0;
    Object.entries(bookings).filter(([d])=>d.startsWith(currentYm)).forEach(([d,s])=>{
      const c=Object.keys(s).length; if(c>busiestCount){busiestCount=c;busiestDay=d;}
    });
    return{totalMembers,todayBooked,upcoming,busiestDay,busiestCount};
  },[bookings,members]);

  const availableMonths=useMemo(()=>{
    const months=[];
    const yr=now.getFullYear();
    for(let m=0;m<=11;m++) months.push({month:m,year:yr,past:m<now.getMonth()});
    return months;
  },[]);

  const calCells=()=>{
    const cells=[];if(!workdays.length)return cells;
    const dow=new Date(workdays[0]).getDay(); // 0=Sun
    for(let i=0;i<dow;i++)cells.push({type:"empty",id:`e${i}`});
    workdays.forEach(date=>cells.push({type:"day",date}));
    return cells;
  };

  const capPct=Math.min(100,Math.round((usedThisMonth/capThisMonth)*100));
  const strength=pwdStrength(authPass);

  if(!loaded) return<><style>{css}</style><div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",fontFamily:"var(--font)",color:"var(--red)",opacity:.35,fontSize:".72rem",letterSpacing:".2em",textTransform:"uppercase"}}>Loading</div></>;

  // ── AUTH SCREENS ──
  if(!user){
    const sw=s=>{setAuthScreen(s);setAuthErr("");setAuthOk("");};
    const NavBar=()=><nav className="nav"><img src={LOGO_SRC} alt="Yours Only Studios" style={{height:"36px",width:"auto"}}/></nav>;
    if(authScreen==="login") return(
      <><style>{css}</style><div className="app"><NavBar/>
      <div className="auth-wrap"><div className="auth-card">
        <h1 className="auth-title">Log in</h1>
        <p className="auth-sub">Enter your email and password to continue.</p>
        {authErr&&<div className="error-msg">{authErr}</div>}
        {authOk&&<div className="success-msg">{authOk}</div>}
        <div className="field"><label>E-mail</label><input type="email" value={authEmail} onChange={e=>setAuthEmail(e.target.value)} placeholder="you@example.com" onKeyDown={e=>e.key==="Enter"&&handleLogin()}/></div>
        <div className="field"><label>Password</label><input type="password" value={authPass} onChange={e=>setAuthPass(e.target.value)} placeholder="Password" onKeyDown={e=>e.key==="Enter"&&handleLogin()}/></div>
        <button className="btn-primary" onClick={handleLogin}>Login</button>
        <div className="auth-links">
          <button className="auth-link" onClick={()=>sw("forgot")}>Forgot your password?</button>
          <button className="auth-link" onClick={()=>sw("signup")}>Create account →</button>
        </div>
      </div></div></div></>
    );
    if(authScreen==="signup") return(
      <><style>{css}</style><div className="app"><NavBar/>
      <div className="auth-wrap"><div className="auth-card">
        <h1 className="auth-title">Create account</h1>
        <p className="auth-sub">Join Yours Only Studios to book your desk.</p>
        {authErr&&<div className="error-msg">{authErr}</div>}
        <div className="field"><label>Full name</label><input type="text" value={authName} onChange={e=>setAuthName(e.target.value)} placeholder="Jane Smith"/></div>
        <div className="field"><label>E-mail</label><input type="email" value={authEmail} onChange={e=>setAuthEmail(e.target.value)} placeholder="you@example.com"/></div>
        <div className="field">
          <label>Password</label>
          <input type="password" value={authPass} onChange={e=>setAuthPass(e.target.value)} placeholder="Min. 6 characters"/>
          {authPass&&<div className="pwd-row"><div className="pwd-bar"><div className="pwd-fill" style={{width:`${strength.pct}%`,background:strength.color}}/></div><span className="pwd-label" style={{color:strength.color}}>{strength.label}</span></div>}
        </div>
        <div className="field"><label>Confirm password</label><input type="password" value={authPass2} onChange={e=>setAuthPass2(e.target.value)} placeholder="Repeat password" onKeyDown={e=>e.key==="Enter"&&handleSignup()}/></div>
        <button className="btn-primary" onClick={handleSignup}>Create account</button>
        <div className="auth-links"><button className="auth-link" onClick={()=>sw("login")}>← Back to log in</button></div>
      </div></div></div></>
    );
    if(authScreen==="forgot") return(
      <><style>{css}</style><div className="app"><NavBar/>
      <div className="auth-wrap"><div className="auth-card">
        <h1 className="auth-title">Reset password</h1>
        <p className="auth-sub">Enter your email to receive a reset code.</p>
        {authErr&&<div className="error-msg">{authErr}</div>}
        {authOk&&<div className="success-msg">{authOk}</div>}
        <div className="field"><label>E-mail address</label><input type="email" value={authEmail} onChange={e=>setAuthEmail(e.target.value)} placeholder="you@example.com" onKeyDown={e=>e.key==="Enter"&&handleForgot()}/></div>
        <button className="btn-primary" onClick={handleForgot}>Send reset code</button>
        {authOk&&<>
          <div className="field" style={{marginTop:"1rem"}}><label>Reset code</label><input type="text" value={resetToken} onChange={e=>setResetToken(e.target.value)} placeholder="e.g. AB12CD"/></div>
          <div className="field"><label>New password</label><input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} placeholder="Min. 6 characters"/></div>
          <button className="btn-primary" onClick={handleReset}>Set new password</button>
        </>}
        <div className="auth-links"><button className="auth-link" onClick={()=>sw("login")}>← Back to log in</button></div>
      </div></div></div></>
    );
  }

  const isAdmin=user.role==="admin";

  // ── FLOOR PLAN COMPONENT ──
  const FloorPlan=({date,isMine,readOnly=false})=>{
    const daySlots=slotsFor(date);
    const deskOwners={};
    Object.entries(daySlots).forEach(([email,deskId])=>{deskOwners[deskId]=email;});

    const renderDesk=desk=>{
      const owner=deskOwners[desk.id];
      const isTakenByOther=owner&&(!user||owner!==user.email);
      const isOwnDesk=user&&owner===user.email;
      const isSelected=!readOnly&&selectedDesk===desk.id;
      const assignInfo = getDeskStatus(desk.id, date);
      const isBlocked = assignInfo && !assignInfo.email;
      const isAssignedToOther = assignInfo && assignInfo.email && assignInfo.email !== user?.email;
      const isAssignedToMe = assignInfo && assignInfo.email === user?.email;

      let cls="desk-tile";
      if(isBlocked) cls+=" blocked-tile";
      else if(isAssignedToOther&&!owner) cls+=" assigned-other-tile";
      else if(isTakenByOther) cls+=" taken-tile";
      else if(isOwnDesk) cls+=" mine-tile";
      else if(isSelected) cls+=" sel-tile";

      const ownerName=owner?(members[owner]?.name||owner):"";
      const firstName=ownerName.split(" ")[0];
      const tileStyle=desk.tall?{flex:1,height:"100%",minHeight:"76px"}:{};

      const displayName = isBlocked ? (assignInfo.label||"Blocked") 
        : isAssignedToOther && !owner ? (members[assignInfo.email]?.name?.split(" ")[0]||"Assigned")
        : isTakenByOther ? firstName
        : isOwnDesk ? "You ✓"
        : "";

      return(
        <div key={desk.id} className={cls} style={tileStyle}
          onClick={()=>{if(!readOnly&&!isMine&&!isTakenByOther&&!isBlocked&&!isAssignedToOther)setSelectedDesk(desk.id);}}
          title={isBlocked?(assignInfo.label||"Blocked"):isAssignedToOther?`Assigned to ${members[assignInfo.email]?.name||assignInfo.email}`:isTakenByOther?ownerName:isOwnDesk?"Your booking":`Desk ${desk.id}`}>
          <span className="desk-num">{desk.id}</span>
          {desk.monitor&&<span className="desk-label">Monitor</span>}
          {displayName&&<span className="desk-owner-name">{displayName}</span>}
          {desk.monitor&&<span className="desk-mon-icon">🖥</span>}
        </div>
      );
    };
    const[top,left,mid,solo]=DESK_GROUPS.map(g=>g.desks);
    return(
      <div className="floorplan">
        {!readOnly&&<div className="fp-legend">
          <div className="fp-legend-item"><div className="fp-legend-swatch avail"/>Available</div>
          <div className="fp-legend-item"><div className="fp-legend-swatch taken"/>Taken</div>
          <div className="fp-legend-item"><div className="fp-legend-swatch mine"/>Your desk</div>
          <div className="fp-legend-item"><div className="fp-legend-swatch sel"/>Selected</div>
          <div className="fp-legend-item"><div className="fp-legend-swatch blocked-swatch"/>Blocked</div>
          <div className="fp-legend-item"><div className="fp-legend-swatch assigned-swatch"/>Reserved</div>
        </div>}
        {readOnly&&<div className="fp-legend">
          <div className="fp-legend-item"><div className="fp-legend-swatch avail"/>Free</div>
          <div className="fp-legend-item"><div className="fp-legend-swatch taken"/>Booked</div>
          <div className="fp-legend-item"><div className="fp-legend-swatch blocked-swatch"/>Blocked</div>
          <div className="fp-legend-item"><div className="fp-legend-swatch assigned-swatch"/>Reserved</div>
        </div>}
        <div style={{display:"flex",gap:"1.5rem",alignItems:"flex-start",marginBottom:"1rem",flexWrap:"wrap"}}>
          <div>
            <div className="fp-section-lbl">Lounge</div>
            <div className="fp-beanbags">
              <div className="fp-beanbag">Bean<br/>bag</div>
              <div className="fp-beanbag">Bean<br/>bag</div>
            </div>
          </div>
          <div style={{flex:1,minWidth:"220px"}}>
            <div className="fp-section-lbl">Desks 1–4</div>
            <div style={{display:"flex",gap:"4px",alignItems:"stretch"}}>
              <div style={{flex:"0 0 auto",width:"22%",display:"flex",flexDirection:"column"}}>
                {renderDesk({...top[3],tall:true})}
              </div>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:"4px"}}>
                {renderDesk(top[0])}
                {renderDesk(top[1])}
              </div>
              <div style={{flex:"0 0 auto",width:"22%",display:"flex"}}>
                <div style={{flex:1,display:"flex",flexDirection:"column"}}>
                  {renderDesk({...top[2],tall:true})}
                </div>
              </div>
            </div>
          </div>
          <div className="fp-entrance" style={{alignSelf:"center"}}>Entrance</div>
        </div>
        <div style={{display:"flex",gap:"1rem",flexWrap:"wrap",alignItems:"flex-start"}}>
          <div style={{flex:"1",minWidth:"170px"}}>
            <div className="fp-section-lbl">Desks 5–10</div>
            <div className="fp-bank">{left.map(d=>renderDesk(d))}</div>
          </div>
          <div style={{flex:"1.2",minWidth:"195px"}}>
            <div className="fp-section-lbl">Desks 11–16</div>
            <div className="fp-bank">{mid.map(d=>renderDesk(d))}</div>
          </div>
          <div>
            <div className="fp-section-lbl">Desk 17</div>
            <div style={{width:"62px"}}>{renderDesk(solo[0])}</div>
          </div>
        </div>
      </div>
    );
  };

  // ── CALENDAR VIEW COMPONENT ──
  const CalendarView = () => {
    const cd = new Date(calDate + "T12:00:00");
    const pad2 = n => String(n).padStart(2,"0");
    const fmt = d => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;

    // Get week days
    const getWeekDays = (baseDate) => {
      const dow = baseDate.getDay();
      const weekStart = new Date(baseDate);
      weekStart.setDate(baseDate.getDate() - dow + (dow===0?-6:1)); // Mon
      return Array.from({length:7},(_,i)=>{ const d=new Date(weekStart); d.setDate(weekStart.getDate()+i); return d; });
    };

    const prevM = new Date(cd.getFullYear(),cd.getMonth()-1,1);
    const nextM = new Date(cd.getFullYear(),cd.getMonth()+1,1);
    const prevW = new Date(cd); prevW.setDate(cd.getDate()-7);
    const nextW = new Date(cd); nextW.setDate(cd.getDate()+7);
    const prevD = new Date(cd); prevD.setDate(cd.getDate()-1);
    const nextD = new Date(cd); nextD.setDate(cd.getDate()+1);

    // Room+desk event block colors
    const ROOM_COLOR = "#ee5340";
    const DESK_COLOR = "#2d7a4f";

    // Get room bookings for a date+room
    const getRoomEvents = (dateStr, roomId) =>
      roomBookings.filter(b=>b.date===dateStr&&b.room_id===roomId);

    // Get desk bookings for a date (including assignments/blocks)
    const getDeskEvents = (dateStr) => {
      const events = Object.entries(bookings[dateStr]||{}).map(([email,desk])=>({email,desk,name:members[email]?.name||email,type:"booking"}));
      // Add blocked/assigned desks - check date is within range
      deskAssignments.forEach(a=>{
        const start = a.start_date || "2000-01-01";
        const end = a.end_date || "2099-12-31";
        if(dateStr>=start && dateStr<=end) {
          if(!a.email) {
            // Blocked desk
            if(!events.find(e=>e.desk===a.desk_id&&e.type==="blocked")) {
              events.push({desk:a.desk_id, name:a.label||"Blocked", type:"blocked", color:"#888"});
            }
          } else if(!events.find(e=>e.desk===a.desk_id&&e.email===a.email)) {
            // Assigned desk
            events.push({desk:a.desk_id, email:a.email, name:members[a.email]?.name||a.email, type:"assigned", label:a.label});
          }
        }
      });
      return events;
    };

    // ── WEEK VIEW (room grid) ──
    if(calView==="week"||calView==="month") {
      const days = calView==="week" ? getWeekDays(cd) : getWeekDays(cd);
      const isWeek = calView==="week";

      // For month: show full month as week-by-week with a month grid at top
      if(calView==="month") {
        const year = cd.getFullYear();
        const month = cd.getMonth();
        const firstDay = new Date(year,month,1);
        const lastDay = new Date(year,month+1,0);
        const startDow = firstDay.getDay()===0?6:firstDay.getDay()-1;
        const cells = [];
        for(let i=0;i<startDow;i++) cells.push(null);
        for(let d=1;d<=lastDay.getDate();d++) cells.push(new Date(year,month,d));
        return(
          <div>
            <div style={{display:"flex",alignItems:"center",gap:"1rem",marginBottom:"1.5rem"}}>
              <button className="nav-btn" onClick={()=>setCalDate(fmt(prevM))}>← Prev</button>
              <span style={{fontFamily:"var(--font-hd)",fontSize:"1.3rem",color:"var(--ink)",flex:1,textAlign:"center"}}>{cd.toLocaleDateString("en-AU",{month:"long",year:"numeric"})}</span>
              <button className="nav-btn" onClick={()=>setCalDate(fmt(nextM))}>Next →</button>
            </div>
            {/* Month mini grid */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:"3px",marginBottom:"2rem"}}>
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d=>(
                <div key={d} style={{textAlign:"center",fontSize:".6rem",fontWeight:800,letterSpacing:".1em",textTransform:"uppercase",color:"var(--ink)",padding:".4rem 0"}}>{d}</div>
              ))}
              {cells.map((d,i)=>{
                if(!d) return <div key={"e"+i}/>;
                const ds=fmt(d);
                const isToday=ds===todayStr;
                const deskCount=Object.keys(bookings[ds]||{}).length;
                const roomCount=roomBookings.filter(b=>b.date===ds).length;
                const total=deskCount+roomCount;
                return(
                  <div key={ds} onClick={()=>{setCalDate(ds);setCalView("week");}}
                    style={{border:isToday?"2px solid var(--red)":"1px solid var(--line)",minHeight:"52px",padding:".3rem .4rem",cursor:"pointer",background:ds===calDate?"var(--red-lt)":"var(--bg)",borderRadius:"2px",transition:"all .12s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor="var(--red)"}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=isToday?"var(--red)":ds===calDate?"var(--red)":"var(--line)"}>
                    <div style={{fontSize:".78rem",fontWeight:700,color:isToday?"var(--red)":"var(--ink)"}}>{d.getDate()}</div>
                    {total>0&&<div style={{display:"flex",gap:"2px",marginTop:"2px",flexWrap:"wrap"}}>
                      {deskCount>0&&<div style={{width:"8px",height:"8px",borderRadius:"50%",background:DESK_COLOR}}/>}
                      {roomCount>0&&<div style={{width:"8px",height:"8px",borderRadius:"50%",background:ROOM_COLOR}}/>}
                      {total>2&&<div style={{fontSize:".55rem",color:"var(--ink)",fontWeight:700}}>+{total}</div>}
                    </div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      // WEEK ROOM GRID VIEW
      const weekLabel = `${days[0].toLocaleDateString("en-AU",{day:"numeric",month:"short"})} – ${days[6].toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"})}`;
      const COL_W = "calc((100% - 120px) / 7)";
      return(
        <div>
          <div style={{display:"flex",alignItems:"center",gap:"1rem",marginBottom:"1.5rem",flexWrap:"wrap"}}>
            <button className="nav-btn" onClick={()=>setCalDate(fmt(prevW))}>← Prev</button>
            <span style={{fontFamily:"var(--font-hd)",fontSize:"1.1rem",color:"var(--ink)",flex:1,textAlign:"center"}}>{weekLabel}</span>
            <button className="nav-btn" onClick={()=>setCalDate(fmt(nextW))}>Next →</button>
          </div>

          {/* Header row */}
          <div style={{display:"grid",gridTemplateColumns:`120px repeat(7,1fr)`,gap:"1px",background:"var(--line)",border:"1px solid var(--line)",marginBottom:"1px"}}>
            <div style={{background:"var(--off)",padding:".6rem .75rem",fontSize:".62rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"var(--ink)"}}> </div>
            {days.map(d=>{
              const ds=fmt(d);
              const isToday=ds===todayStr;
              return(
                <div key={ds} style={{background:isToday?"var(--red-lt)":"var(--off)",padding:".6rem .5rem",textAlign:"center",cursor:"pointer"}} onClick={()=>{setCalDate(ds);setCalView("day");}}>
                  <div style={{fontSize:".6rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:isToday?"var(--red)":"var(--ink)"}}>{d.toLocaleDateString("en-AU",{weekday:"short"})}</div>
                  <div style={{fontSize:"1.1rem",fontWeight:700,color:isToday?"var(--red)":"var(--ink)"}}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>

          {/* Room rows */}
          {ROOMS.map(room=>(
            <div key={room.id} style={{display:"grid",gridTemplateColumns:"120px repeat(7,1fr)",gap:"1px",background:"var(--line)",border:"1px solid var(--line)",borderTop:"none"}}>
              <div style={{background:"var(--off)",padding:".75rem .6rem",display:"flex",alignItems:"center",gap:".35rem"}}>
                <span style={{fontSize:".9rem"}}>{room.icon}</span>
                <span style={{fontSize:".65rem",fontWeight:700,color:"var(--ink)",lineHeight:1.3}}>{room.name}</span>
              </div>
              {days.map(d=>{
                const ds=fmt(d);
                const isToday=ds===todayStr;
                const events=getRoomEvents(ds,room.id);
                return(
                  <div key={ds} style={{background:isToday?"#fdfaf9":"white",padding:".4rem .3rem",minHeight:"80px",verticalAlign:"top"}}>
                    {events.map(b=>(
                      <div key={b.id} onClick={user&&(b.email===user.email||isAdmin)?()=>{setEditBookingModal({type:"room",item:b});setEditStartTime(b.start_time);setEditEndTime(b.end_time);setEditDate(b.date);}:undefined} style={{background:ROOM_COLOR,color:"white",borderRadius:"2px",padding:".3rem .4rem",marginBottom:"3px",fontSize:".65rem",lineHeight:1.3,cursor:user&&(b.email===user.email||isAdmin)?"pointer":"default"}}>
                        <div style={{fontWeight:700,opacity:.85}}>{fmtTime(b.start_time)} – {fmtTime(b.end_time)}</div>
                        <div style={{fontWeight:600}}>{b.name}{user&&(b.email===user.email||isAdmin)?" ✎":""}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Desk row */}
          <div style={{display:"grid",gridTemplateColumns:"120px repeat(7,1fr)",gap:"1px",background:"var(--line)",border:"1px solid var(--line)",borderTop:"none"}}>
            <div style={{background:"var(--off)",padding:".75rem .6rem",display:"flex",alignItems:"center",gap:".35rem"}}>
              <span style={{fontSize:".9rem"}}>🪑</span>
              <span style={{fontSize:".65rem",fontWeight:700,color:"var(--ink)",lineHeight:1.3}}>Desks</span>
            </div>
            {days.map(d=>{
              const ds=fmt(d);
              const isToday=ds===todayStr;
              const events=getDeskEvents(ds);
              return(
                <div key={ds} style={{background:isToday?"#fdfaf9":"white",padding:".4rem .3rem",minHeight:"80px"}}>
                  {events.map((e,i)=>{
                    const bg = e.type==="blocked"?"#888":e.type==="assigned"?"#c8860a":DESK_COLOR;
                    return <div key={i} onClick={e.type==="booking"&&user&&(e.email===user.email||isAdmin)?()=>{setEditBookingModal({type:"desk",item:{date:ds,desk:e.desk,email:e.email}});setEditDesk(e.desk);setEditDate(ds);}:undefined} style={{background:bg,color:"white",borderRadius:"2px",padding:".3rem .4rem",marginBottom:"3px",fontSize:".65rem",lineHeight:1.3,cursor:e.type==="booking"&&user&&(e.email===user.email||isAdmin)?"pointer":"default"}}>
                      <div style={{fontWeight:700,opacity:.85}}>{e.type==="blocked"?"🚫":e.type==="assigned"?"📌":""} Desk {e.desk}</div>
                      <div style={{fontWeight:600}}>{e.name}{e.label?` · ${e.label}`:""}{e.type==="booking"&&user&&(e.email===user.email||isAdmin)?" ✎":""}</div>
                    </div>;
                  })}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // DAY VIEW
    const label2 = cd.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
    const deskEvts = getDeskEvents(calDate);
    return(
      <div>
        <div style={{display:"flex",alignItems:"center",gap:"1rem",marginBottom:"1.5rem"}}>
          <button className="nav-btn" onClick={()=>setCalDate(fmt(prevD))}>← Prev</button>
          <span style={{fontFamily:"var(--font-hd)",fontSize:"1.3rem",color:"var(--ink)",flex:1,textAlign:"center"}}>{label2}</span>
          <button className="nav-btn" onClick={()=>setCalDate(fmt(nextD))}>Next →</button>
        </div>

        {/* Time grid */}
        <div style={{display:"grid",gridTemplateColumns:"60px 1fr",gap:"1px",background:"var(--line)",border:"1px solid var(--line)"}}>
          {/* header */}
          <div style={{background:"var(--off)",padding:".5rem"}}/>
          <div style={{background:"var(--off)",padding:".5rem .75rem",display:"grid",gridTemplateColumns:`repeat(${ROOMS.length+1},1fr)`,gap:"4px"}}>
            {ROOMS.map(r=><div key={r.id} style={{fontSize:".6rem",fontWeight:700,color:"var(--ink)",textAlign:"center"}}>{r.icon} {r.name}</div>)}
            <div style={{fontSize:".6rem",fontWeight:700,color:"var(--ink)",textAlign:"center"}}>🪑 Desks</div>
          </div>
          {/* time slots */}
          {TIME_SLOTS.slice(0,-1).map(slot=>{
            const isHour = slot.endsWith(":00");
            return(<>
              <div key={"t"+slot} style={{background:isHour?"var(--off)":"white",padding:".35rem .4rem",textAlign:"right",fontSize:".65rem",color:"var(--ink)",fontWeight:isHour?700:400,opacity:isHour?1:.5,borderTop:isHour?"1px solid var(--line)":"none"}}>{isHour?fmtTime(slot):""}</div>
              <div key={"c"+slot} style={{background:"white",borderTop:isHour?"1px solid var(--line)":"1px solid #f0f0f0",padding:".2rem .4rem",minHeight:"24px",display:"grid",gridTemplateColumns:`repeat(${ROOMS.length+1},1fr)`,gap:"4px"}}>
                {ROOMS.map(room=>{
                  const b=roomBookings.find(rb=>rb.room_id===room.id&&rb.date===calDate&&rb.start_time===slot);
                  return <div key={room.id}>{b&&<div style={{background:ROOM_COLOR,color:"white",borderRadius:"2px",padding:".2rem .35rem",fontSize:".62rem",lineHeight:1.2}}><div style={{fontWeight:700}}>{b.name}</div><div style={{opacity:.8}}>{fmtTime(b.start_time)}–{fmtTime(b.end_time)}</div></div>}</div>;
                })}
                <div>{deskEvts.length>0&&slot==="08:00"&&deskEvts.map((e,i)=>(
                  <div key={i} style={{background:DESK_COLOR,color:"white",borderRadius:"2px",padding:".2rem .35rem",marginBottom:"2px",fontSize:".62rem",lineHeight:1.2}}>
                    <div style={{fontWeight:700}}>Desk {e.desk}</div>
                    <div style={{opacity:.8}}>{e.name}</div>
                  </div>
                ))}</div>
              </div>
            </>);
          })}
        </div>
      </div>
    );
  };

    // ── ROOM BOOKING HELPERS ──
  const maxRoomDate = (()=>{ const d=new Date(); d.setMonth(d.getMonth()+(isAdmin?12:5)); return fmtDate(d); })();

  const getRoomBookingsForDate = (date) => roomBookings.filter(b=>b.date===date);

  const isSlotTaken = (roomId, date, slot) =>
    roomBookings.some(b=>{
      if(b.room_id!==roomId||b.date!==date) return false;
      const startIdx = TIME_SLOTS.indexOf(b.start_time);
      const endIdx = TIME_SLOTS.indexOf(b.end_time);
      const slotIdx = TIME_SLOTS.indexOf(slot);
      return slotIdx>=startIdx && slotIdx<endIdx;
    });

  const myRoomBookings = user ? roomBookings.filter(b=>b.email===user.email&&b.date>=todayStr).sort((a,b)=>a.date.localeCompare(b.date)||a.start_time.localeCompare(b.start_time)) : [];

  const confirmRoomBooking = async () => {
    if(!roomModal) return;
    const bookingName = (roomName||"").trim() || user.name;
    const {room,date,slot} = roomModal;
    const endIdx = TIME_SLOTS.indexOf(slot)+roomDuration;
    const endTime = TIME_SLOTS[endIdx]||"20:00";
    const booking = {
      id: Date.now().toString(),
      room_id: room.id,
      room_name: room.name,
      date,
      start_time: slot,
      end_time: endTime,
      email: user.email,
      name: bookingName,
    };
    await addRoomBooking(booking);
    setRoomBookings(r=>[...r,booking]);
    setRoomModal(null);
    setRoomName("");
    setRoomDuration(1);
    // Confirmation email to booker
    const d=new Date(date+"T12:00:00");
    const dateStr=d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
    const emailHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>body{margin:0;padding:0;background:#f5f5f3;font-family:'DM Sans',sans-serif;}.wrap{max-width:560px;margin:40px auto;background:#fff;border-radius:4px;overflow:hidden;}.banner{background:#ee5340;padding:32px 40px;}.banner-logo{color:#fff;font-family:Georgia,serif;font-size:1.3rem;}.banner-title{color:#fff;font-family:Georgia,serif;font-size:1.9rem;font-weight:600;margin:8px 0 0;}.body{padding:36px 40px;}.greeting{font-size:1rem;color:#1a1a1a;margin:0 0 24px;}.detail-box{background:#f5f5f3;border-radius:4px;padding:20px 24px;margin:0 0 28px;}.detail-row{display:flex;justify-content:space-between;padding:6px 0;font-size:.9rem;color:#1a1a1a;border-bottom:1px solid #e8e8e6;}.detail-row:last-child{border-bottom:none;}.detail-label{color:#888;}.footer{padding:20px 40px;border-top:1px solid #e8e8e6;font-size:.8rem;color:#aaa;}</style></head><body><div class="wrap"><div class="banner"><div class="banner-logo">Yours Only Studios</div><div class="banner-title">Room booking confirmed</div></div><div class="body"><p class="greeting">Hi ${booking.name}, your room booking is confirmed.</p><div class="detail-box"><div class="detail-row"><span class="detail-label">Room</span><span>${room.name}</span></div><div class="detail-row"><span class="detail-label">Date</span><span>${dateStr}</span></div><div class="detail-row"><span class="detail-label">Time</span><span>${fmtTime(slot)} – ${fmtTime(endTime)}</span></div></div></div><div class="footer">Yours Only Studios</div></div></body></html>`;
    sendEmail({ to: user.email, subject: `Room booking confirmed – ${room.name} ${date}`, html: emailHtml });
    sendEmail({ to: "studio@yoursonly.com", subject: `New room booking – ${room.name} ${date} ${fmtTime(slot)}`, html: emailHtml });
  };

  const cancelRoomBooking = async (booking) => {
    await removeRoomBooking(booking.id);
    setRoomBookings(r=>r.filter(b=>b.id!==booking.id));
    const d=new Date(booking.date+"T12:00:00");
    const dateStr=d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
    const emailHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>body{margin:0;padding:0;background:#f5f5f3;font-family:'DM Sans',sans-serif;}.wrap{max-width:560px;margin:40px auto;background:#fff;border-radius:4px;overflow:hidden;}.banner{background:#1a1a1a;padding:32px 40px;}.banner-logo{color:#fff;font-family:Georgia,serif;font-size:1.3rem;}.banner-title{color:#ee5340;font-family:Georgia,serif;font-size:1.9rem;font-weight:600;margin:8px 0 0;}.body{padding:36px 40px;}.greeting{font-size:1rem;color:#1a1a1a;margin:0 0 24px;}.detail-box{background:#f5f5f3;border-radius:4px;padding:20px 24px;margin:0 0 28px;}.detail-row{display:flex;justify-content:space-between;padding:6px 0;font-size:.9rem;color:#1a1a1a;border-bottom:1px solid #e8e8e6;}.detail-row:last-child{border-bottom:none;}.detail-label{color:#888;}.footer{padding:20px 40px;border-top:1px solid #e8e8e6;font-size:.8rem;color:#aaa;}</style></head><body><div class="wrap"><div class="banner"><div class="banner-logo">Yours Only Studios</div><div class="banner-title">Room booking cancelled</div></div><div class="body"><p class="greeting">Hi ${booking.name}, your room booking has been cancelled.</p><div class="detail-box"><div class="detail-row"><span class="detail-label">Room</span><span>${booking.room_name}</span></div><div class="detail-row"><span class="detail-label">Date</span><span>${dateStr}</span></div><div class="detail-row"><span class="detail-label">Time</span><span>${fmtTime(booking.start_time)} – ${fmtTime(booking.end_time)}</span></div></div></div><div class="footer">Yours Only Studios</div></div></body></html>`;
    sendEmail({ to: booking.email, subject: `Room booking cancelled – ${booking.room_name} ${booking.date}`, html: emailHtml });
    sendEmail({ to: "studio@yoursonly.com", subject: `Room booking cancelled – ${booking.room_name} ${booking.date} ${fmtTime(booking.start_time)}`, html: emailHtml });
  };

  // ── MAIN APP ──
  return(
    <><style>{css}</style>
    <div className="app">
      <nav className="nav">
        <img src={LOGO_SRC} alt="Yours Only Studios" style={{height:"36px",width:"auto"}}/>
        <div className="nav-right">
          <span className="nav-user">{user.name}</span>
          <button className="nav-btn" onClick={()=>setUser(null)}>Sign out</button>
        </div>
      </nav>

      <div className="main">
        <div className="tabs">
          <button className={`tab${tab==="book"?" active":""}`} onClick={()=>setTab("book")}>Book a desk</button>
          <button className={`tab${tab==="mine"?" active":""}`} onClick={()=>setTab("mine")}>My bookings{myBookings.length>0?` (${myBookings.length})`:""}</button>
          <button className={`tab${tab==="rooms"?" active":""}`} onClick={()=>setTab("rooms")}>Book a room{myRoomBookings.length>0?` (${myRoomBookings.length})`:""}</button>
          <button className={`tab${tab==="calendar"?" active":""}`} onClick={()=>setTab("calendar")}>Calendar</button>
          {isAdmin&&<button className={`tab${tab==="admin"?" active":""}`} onClick={()=>setTab("admin")}>Admin</button>}
        </div>

        {tab!=="admin"&&(
          <div className="status-bar">
            <div className="status-item"><div className="status-label">Month</div><div className="status-val">{MONTH_SHORT[viewMonth]}</div></div>
            <div className="status-item"><div className="status-label">Used</div><div className="status-val">{usedThisMonth}</div></div>
            <div className="status-item"><div className="status-label">Remaining</div><div className={`status-val${remaining===0?" bad":remaining<=2?" warn":""}`}>{remaining}</div></div>
            <div className="status-item"><div className="status-label">Monthly cap</div><div className="status-val">{capThisMonth}</div></div>
            <div className="cap-bar-wrap">
              <div className="cap-bar-label"><span>Usage this month</span><span>{capPct}%</span></div>
              <div className="cap-bar"><div className="cap-bar-fill" style={{width:`${capPct}%`}}/></div>
            </div>
          </div>
        )}

        {/* ── BOOK TAB ── */}
        {tab==="book"&&<>
          <div className="year-strip">
            {availableMonths.map(({month,year,past})=>(
              <button key={month} className={`month-pill${viewMonth===month&&viewYear===year?" active":""}${past?" past":""}`}
                onClick={()=>{setViewMonth(month);setViewYear(year);}}>
                {MONTH_SHORT[month]}
              </button>
            ))}
          </div>

          <div className="cal-wrap">
          <div className="home-split">
            {/* LEFT: calendar */}
            <div>
              <div className="cal-header">
                <span className="cal-title">{MONTH_NAMES[viewMonth]}</span>
                <span className="cal-year">{viewYear}</span>
              </div>
              <div className="cal-grid">
                {DAY_LABELS.map((d,i)=>(
                  <div key={d} className={`cal-day-label${i===0||i===6?" weekend":""}`}>{d}</div>
                ))}
                {calCells().map(cell=>{
                  if(cell.type==="empty")return<div key={cell.id}/>;
                  const{date}=cell;
                  const isPast=date<todayStr;
                  const booked=userHasDay(date);
                  const full=countBooked(date)>=DESK_TOTAL;
                  const used=daysInMonthBooked(bookings,user.email,date.slice(0,7));
                  const cap=members[user.email]?.cap??user.cap;
                  const noCapLeft=used>=cap&&!booked;
                  const avail=DESK_TOTAL-countBooked(date);
                  const isPreview=previewDate===date&&!booked;
                  const d=new Date(date+"T12:00:00");
                  const dow=d.getDay();
                  const isWeekend=dow===0||dow===6;
                  const cls=["cal-cell",isPast?"past":"",full&&!booked?"full":"",booked?"booked":"",date===todayStr?"today-cell":"",noCapLeft&&!booked?"disabled":"",isPreview?"preview-cell":"",isWeekend&&!booked&&!isPast?"weekend-cell":""].filter(Boolean).join(" ");
                  return(
                    <div key={date} className={cls}
                      onClick={()=>!isPast&&openModal(date)}
                      onMouseEnter={()=>!isPast&&setPreviewDate(date)}>
                      <span className="cal-date">{parseInt(date.slice(8))}{date===todayStr?" ·":""}</span>
                      {booked?<span className="cal-booked-tag">✓ Booked</span>:full?<span className="cal-full-tag">Full</span>:<span className="cal-spots">{avail} free</span>}
                    </div>
                  );
                })}
              </div>
              <p className="cal-hint">Hover over a date to preview desks. Click to book. Studio open 7 days.</p>
            </div>

            {/* RIGHT: live floor plan preview */}
            <div className="home-floor-panel">
              <div className="home-floor-title">Studio floor plan</div>
              {(()=>{
                const pd=previewDate||todayStr;
                const d=new Date(pd+"T12:00:00");
                const label=d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long"});
                const daySlots=slotsFor(pd);
                const bookedCount=Object.keys(daySlots).length;
                const freeCount=DESK_TOTAL-bookedCount;
                const isPastDay=pd<todayStr;
                return(
                  <>
                    <div className="home-floor-sub">{label}</div>
                    <div className="home-floor-stats">
                      <div className="home-floor-stat">
                        <div className="home-floor-stat-label">Free desks</div>
                        <div className="home-floor-stat-val" style={{color:"var(--red)"}}>{freeCount}</div>
                      </div>
                      <div className="home-floor-stat">
                        <div className="home-floor-stat-label">Booked</div>
                        <div className="home-floor-stat-val">{bookedCount}</div>
                      </div>
                    </div>
                    <FloorPlan date={pd} isMine={userHasDay(pd)} readOnly={false} compact={true}/>
                    {!isPastDay&&!userHasDay(pd)&&freeCount>0&&<div style={{marginTop:".75rem",fontSize:".75rem",color:"#aaa",textAlign:"center"}}>Click a day on the calendar to book</div>}
                    {!isPastDay&&userHasDay(pd)&&<div style={{marginTop:".75rem",fontSize:".75rem",color:"var(--red)",textAlign:"center",opacity:.7}}>✓ You have desk {daySlots[user.email]} booked</div>}
                    {!isPastDay&&freeCount===0&&!userHasDay(pd)&&<div style={{marginTop:".75rem",fontSize:".75rem",color:"#aaa",textAlign:"center"}}>Studio full this day</div>}
                  </>
                );
              })()}
            </div>
          </div>
          </div>
        </>}

        {/* ── MY BOOKINGS TAB ── */}
        {tab==="mine"&&(()=>{
          // Combine desk + room bookings, group by week
          const allMyBookings = [
            ...myBookings.map(b=>({...b, kind:"desk"})),
            ...myRoomBookings.map(b=>({date:b.date, kind:"room", booking:b}))
          ].sort((a,b)=>a.date.localeCompare(b.date));

          if(!allMyBookings.length) return <div className="empty-state"><p>No upcoming bookings</p><span>Head to the calendar to book a desk or room.</span></div>;

          // Group by week
          const weeks = {};
          allMyBookings.forEach(item=>{
            const d = new Date(item.date+"T12:00:00");
            const dow = d.getDay();
            const diff = d.getDate() - dow + (dow===0?-6:1);
            const mon = new Date(d); mon.setDate(diff);
            const wk = `${mon.getFullYear()}-${String(mon.getMonth()+1).padStart(2,"0")}-${String(mon.getDate()).padStart(2,"0")}`;
            if(!weeks[wk]) weeks[wk]=[];
            weeks[wk].push(item);
          });

          // Current week key
          const nowD2 = new Date();
          const nd = nowD2.getDay();
          const diff2 = nowD2.getDate()-nd+(nd===0?-6:1);
          const curMon2 = new Date(nowD2); curMon2.setDate(diff2);
          const curWk = `${curMon2.getFullYear()}-${String(curMon2.getMonth()+1).padStart(2,"0")}-${String(curMon2.getDate()).padStart(2,"0")}`;

          return <div>{Object.entries(weeks).sort(([a],[b])=>a.localeCompare(b)).map(([wk,items])=>{
            const mon2 = new Date(wk+"T12:00:00");
            const sun2 = new Date(mon2); sun2.setDate(mon2.getDate()+6);
            const wkLabel = `${mon2.toLocaleDateString("en-AU",{day:"numeric",month:"short"})} – ${sun2.toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"})}`;
            const isCurWk = wk===curWk;
            const isOpen2 = openMyWeeks[wk]!==undefined ? openMyWeeks[wk] : isCurWk;
            return(
              <div key={wk} style={{marginBottom:"1rem",border:"1px solid var(--line)",overflow:"hidden"}}>
                <div onClick={()=>toggleMyWeek(wk)} style={{background:"var(--off)",padding:".75rem 1.25rem",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:isOpen2?"1px solid var(--line)":"none",cursor:"pointer",userSelect:"none"}}>
                  <div style={{display:"flex",alignItems:"center",gap:".75rem"}}>
                    <span style={{fontSize:".9rem",color:"var(--ink)",display:"inline-block",transform:isOpen2?"rotate(90deg)":"rotate(0deg)",transition:"transform .15s"}}>›</span>
                    <span style={{fontSize:".72rem",fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:"var(--ink)"}}>{wkLabel}</span>
                    {isCurWk&&<span style={{fontSize:".6rem",background:"var(--red)",color:"white",padding:".15rem .5rem",letterSpacing:".08em",textTransform:"uppercase"}}>This week</span>}
                  </div>
                  <span style={{fontSize:".72rem",color:"var(--red)",fontWeight:600}}>{items.length} booking{items.length!==1?"s":""}</span>
                </div>
                {isOpen2&&<div className="bookings-list" style={{border:"none"}}>
                  {items.map((item,i)=>{
                    const d=new Date(item.date+"T12:00:00");
                    const label=d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
                    if(item.kind==="desk") return(
                      <div key={item.date} className="booking-row">
                        <div><div className="booking-date">{label}</div><div className="booking-meta">Desk {item.desk}</div></div>
                        <div style={{display:"flex",alignItems:"center",gap:"1rem"}}>
                          <span className="booking-desk-badge">Desk {item.desk}</span>
                          <button className="btn-cancel-booking" style={{color:"#2d7a4f"}} onClick={()=>{setEditBookingModal({type:'desk',item});setEditDesk(item.desk);setEditDate(item.date);}}>Edit</button><button className="btn-cancel-booking" onClick={()=>cancelBooking(item.date)}>Cancel</button>
                        </div>
                      </div>
                    );
                    const b=item.booking;
                    const room=ROOMS.find(r=>r.id===b.room_id);
                    return(
                      <div key={b.id} className="booking-row" style={{borderLeft:"3px solid var(--mint-txt)"}}>
                        <div><div className="booking-date">{label}</div><div className="booking-meta">{room?.icon} {b.room_name} · {fmtTime(b.start_time)}–{fmtTime(b.end_time)}</div></div>
                        <div style={{display:"flex",alignItems:"center",gap:"1rem"}}>
                          <span className="booking-desk-badge" style={{color:"var(--mint-txt)",background:"var(--mint)",borderColor:"rgba(45,122,79,.2)"}}>{fmtTime(b.start_time)}</span>
                          <button className="btn-cancel-booking" style={{color:"#2d7a4f"}} onClick={()=>{setEditBookingModal({type:"room",item:b});setEditStartTime(b.start_time);setEditEndTime(b.end_time);setEditDate(b.date);}}>Edit</button><button className="btn-cancel-booking" onClick={()=>cancelRoomBooking(b)}>Cancel</button>
                        </div>
                      </div>
                    );
                  })}
                </div>}
              </div>
            );
          })}</div>;
        })()}

        {/* ── CALENDAR TAB ── */}
        {tab==="calendar"&&<>
          <div style={{display:"flex",alignItems:"center",gap:".5rem",marginBottom:"1.5rem",flexWrap:"wrap"}}>
            <div style={{display:"flex",border:"1px solid var(--line)",overflow:"hidden"}}>
              {["day","week","month"].map(v=>(
                <button key={v} onClick={()=>setCalView(v)} style={{padding:".45rem 1rem",background:calView===v?"var(--red)":"transparent",color:calView===v?"white":"var(--ink)",border:"none",borderRight:v!=="month"?"1px solid var(--line)":"none",cursor:"pointer",fontFamily:"var(--font)",fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",transition:"all .15s"}}>
                  {v}
                </button>
              ))}
            </div>
            <input type="date" className="day-input" value={calDate} onChange={e=>setCalDate(e.target.value)} style={{fontSize:".82rem"}}/>
            <button className="nav-btn" onClick={()=>setCalDate(today())}>Today</button>
          </div>
          <CalendarView/>
        </>}

        {/* ── ROOMS TAB ── */}
        {tab==="rooms"&&<>
          <div style={{marginBottom:"1.5rem"}}>
            <div style={{display:"flex",gap:"4px",marginBottom:"1.5rem"}}>
              <button className={`month-pill${roomTab==="book"?" active":""}`} onClick={()=>setRoomTab("book")}>Book a room</button>
              <button className={`month-pill${roomTab==="mine"?" active":""}`} onClick={()=>setRoomTab("mine")}>My bookings{myRoomBookings.length>0?` (${myRoomBookings.length})`:""}</button>
            </div>

            {roomTab==="book"&&<>
              <div style={{display:"flex",alignItems:"center",gap:"1rem",marginBottom:"1.5rem",flexWrap:"wrap"}}>
                <div>
                  <label style={{display:"block",fontSize:".75rem",color:"var(--ink)",fontWeight:600,marginBottom:".4rem"}}>Select date</label>
                  <input type="date" className="day-input" value={roomDate} min={todayStr} max={maxRoomDate} onChange={e=>setRoomDate(e.target.value)}/>
                </div>
                <div style={{fontSize:".82rem",color:"var(--ink)",opacity:.5,marginTop:"1.2rem"}}>
                  {(()=>{const d=new Date(roomDate+"T12:00:00");return d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long"});})()}
                </div>
              </div>

              {/* Room grid */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"1rem"}} className="room-grid">
                {ROOMS.map(room=>{
                  const dayBookings = getRoomBookingsForDate(roomDate).filter(b=>b.room_id===room.id);
                  return(
                    <div key={room.id} style={{border:"1px solid var(--line)",overflow:"hidden"}}>
                      <div style={{background:"var(--ink)",color:"white",padding:".85rem 1rem",display:"flex",alignItems:"center",gap:".5rem",minHeight:"52px"}}>
                        <span style={{fontSize:"1rem",flexShrink:0}}>{room.icon}</span>
                        <span style={{fontFamily:"var(--font-hd)",fontSize:".88rem",fontWeight:400,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{room.name}</span>
                      </div>
                      <div style={{padding:".75rem"}}>
                        {TIME_SLOTS.slice(0,-1).map(slot=>{
                          const taken = isSlotTaken(room.id, roomDate, slot);
                          const myBooking = roomBookings.find(b=>b.room_id===room.id&&b.date===roomDate&&b.start_time===slot&&b.email===user.email);
                          const endIdx = TIME_SLOTS.indexOf(slot)+1;
                          const endTime = TIME_SLOTS[endIdx]||"20:00";
                          return(
                            <div key={slot} onClick={()=>{if(!taken)setRoomModal({room,date:roomDate,slot});}}
                              style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:".4rem .6rem",marginBottom:"2px",cursor:taken?"default":"pointer",background:myBooking?"var(--red-lt)":taken?"#f0f0f0":"white",border:myBooking?"1px solid var(--red)":taken?"1px solid #e0e0e0":"1px solid transparent",borderRadius:"2px",transition:"all .12s"}}
                              onMouseEnter={e=>{if(!taken)e.currentTarget.style.borderColor="var(--red)";}}
                              onMouseLeave={e=>{if(!taken&&!myBooking)e.currentTarget.style.borderColor="transparent";}}>
                              <span style={{fontSize:".75rem",fontWeight:600,color:"var(--ink)"}}>{fmtTime(slot)}</span>
                              {myBooking
                                ? <span style={{fontSize:".65rem",color:"var(--red)",fontWeight:700,letterSpacing:".06em",textTransform:"uppercase"}}>You ✓</span>
                                : taken
                                  ? <span style={{fontSize:".65rem",color:"#555",fontWeight:600}}>{(()=>{
                                      const b=roomBookings.find(rb=>{
                                        if(rb.room_id!==room.id||rb.date!==roomDate) return false;
                                        const si=TIME_SLOTS.indexOf(rb.start_time);
                                        const ei=TIME_SLOTS.indexOf(rb.end_time);
                                        const sli=TIME_SLOTS.indexOf(slot);
                                        return sli>=si&&sli<ei;
                                      });
                                      return b?.name||"Booked";
                                    })()}</span>
                                  : <span style={{fontSize:".65rem",color:"#aaa"}}>Free</span>
                              }
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>}

            {roomTab==="mine"&&(
              myRoomBookings.length===0
                ?<div className="empty-state"><p>No upcoming room bookings</p><span>Head to Book a room to make a booking.</span></div>
                :<div className="bookings-list">
                  {myRoomBookings.map(b=>{
                    const d=new Date(b.date+"T12:00:00");
                    const label=d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
                    const room=ROOMS.find(r=>r.id===b.room_id);
                    return(
                      <div key={b.id} className="booking-row">
                        <div>
                          <div className="booking-date">{label}</div>
                          <div className="booking-meta">{room?.icon} {b.room_name} · {fmtTime(b.start_time)} – {fmtTime(b.end_time)}</div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:"1rem"}}>
                          <span className="booking-desk-badge">{fmtTime(b.start_time)}</span>
                          <button className="btn-cancel-booking" style={{color:"#2d7a4f"}} onClick={()=>{setEditBookingModal({type:"room",item:b});setEditStartTime(b.start_time);setEditEndTime(b.end_time);setEditDate(b.date);}}>Edit</button><button className="btn-cancel-booking" onClick={()=>cancelRoomBooking(b)}>Cancel</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
            )}
          </div>
        </>}

        {/* ── ADMIN TAB ── */}
        {tab==="admin"&&isAdmin&&<>

          {/* Stats */}
          <div className="admin-grid">
            <div className="admin-stat">
              <div className="admin-stat-label">Members</div>
              <div className="admin-stat-val">{adminStats.totalMembers}</div>
              <div className="admin-stat-sub">Active accounts</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-label">In today</div>
              <div className="admin-stat-val">{adminStats.todayBooked}</div>
              <div className="admin-stat-sub">of {DESK_TOTAL} desks booked</div>
              <div className="occupancy-bar" style={{marginTop:".6rem"}}><div className="occupancy-fill" style={{width:`${Math.round(adminStats.todayBooked/DESK_TOTAL*100)}%`}}/></div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-label">Upcoming bookings</div>
              <div className="admin-stat-val">{adminStats.upcoming.length}</div>
              <div className="admin-stat-sub">from today onwards</div>
            </div>
          </div>

          {/* Day viewer */}
          <div className="admin-section">
            <div className="section-title"><span>Daily floor view</span></div>
            <div className="day-picker-row">
              <input type="date" className="day-input" value={adminDay} onChange={e=>setAdminDay(e.target.value)}/>
              <span style={{fontSize:".8rem",color:"var(--red)",opacity:.5}}>
                {(()=>{const d=new Date(adminDay+"T12:00:00");return d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"});})()}
              </span>
            </div>
            <FloorPlan date={adminDay} isMine={false} readOnly={true}/>
            <div className="day-view-card" style={{marginTop:"1rem"}}>
              <div className="day-view-header">
                <span className="day-view-title">
                  {(()=>{const d=new Date(adminDay+"T12:00:00");return d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long"});})()}
                </span>
                <span className="day-view-stats">{Object.keys(slotsFor(adminDay)).length} of {DESK_TOTAL} desks booked</span>
              </div>
              <div className="day-view-body">
                <div className="day-view-half">
                  <div className="day-view-half-title">Booked</div>
                  {ALL_DESKS.filter(desk=>Object.entries(slotsFor(adminDay)).find(([,did])=>did===desk.id)).length===0
                    ?<div className="day-desk-empty">No bookings yet</div>
                    :ALL_DESKS.filter(desk=>{const slots=slotsFor(adminDay);return Object.entries(slots).find(([,did])=>did===desk.id);}).map(desk=>{
                      const slots=slotsFor(adminDay);
                      const ownerEmail=Object.entries(slots).find(([,did])=>did===desk.id)?.[0];
                      const ownerName=members[ownerEmail]?.name||ownerEmail;
                      return(
                        <div key={desk.id} className="day-desk-row">
                          <span className="day-desk-num">Desk {desk.id}{desk.monitor&&<span className="day-desk-monitor">Mon</span>}</span>
                          <span className="day-desk-name">{ownerName}</span>
                        </div>
                      );
                    })
                  }
                </div>
                <div className="day-view-half">
                  <div className="day-view-half-title">Available</div>
                  {ALL_DESKS.filter(desk=>{const slots=slotsFor(adminDay);return !Object.entries(slots).find(([,did])=>did===desk.id);}).map(desk=>(
                    <div key={desk.id} className="day-desk-row">
                      <span className="day-desk-num">Desk {desk.id}{desk.monitor&&<span className="day-desk-monitor">Mon</span>}</span>
                      <span className="day-desk-empty">Free</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── ROOM BOOKINGS ADMIN ── */}
          <div className="admin-section">
            <div className="section-title"><span>Room bookings — today & upcoming</span></div>
            {(()=>{
              const upcoming = roomBookings.filter(b=>b.date>=todayStr).sort((a,b)=>a.date.localeCompare(b.date)||a.start_time.localeCompare(b.start_time));
              if(!upcoming.length) return <div style={{color:"var(--red)",opacity:.4,fontSize:".85rem"}}>No upcoming room bookings.</div>;
              // Group by month then week
              const rMonths={};
              upcoming.forEach(b=>{
                const d=new Date(b.date+"T12:00:00");
                const mKey=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
                const dow=d.getDay();const diff=d.getDate()-dow+(dow===0?-6:1);
                const mon=new Date(d);mon.setDate(diff);
                const wk=`${mon.getFullYear()}-${String(mon.getMonth()+1).padStart(2,"0")}-${String(mon.getDate()).padStart(2,"0")}`;
                if(!rMonths[mKey])rMonths[mKey]={};
                if(!rMonths[mKey][wk])rMonths[mKey][wk]=[];
                rMonths[mKey][wk].push(b);
              });
              const nowD3=new Date();const nd3=nowD3.getDay();const diff3=nowD3.getDate()-nd3+(nd3===0?-6:1);
              const curMon3=new Date(nowD3);curMon3.setDate(diff3);
              const curWk3=`${curMon3.getFullYear()}-${String(curMon3.getMonth()+1).padStart(2,"0")}-${String(curMon3.getDate()).padStart(2,"0")}`;
              const curMonth3=`${nowD3.getFullYear()}-${String(nowD3.getMonth()+1).padStart(2,"0")}`;
              return <div>{Object.entries(rMonths).sort(([a],[b])=>a.localeCompare(b)).map(([mKey,weeks])=>{
                const [yr,mo]=mKey.split("-");
                const monthLabel=new Date(Number(yr),Number(mo)-1,1).toLocaleDateString("en-AU",{month:"long",year:"numeric"});
                const isCurMonth=mKey===curMonth3;
                const isMonthOpen=openWeeks["rm_"+mKey]!==undefined?openWeeks["rm_"+mKey]:isCurMonth;
                const totalInMonth=Object.values(weeks).flat().length;
                return(
                  <div key={mKey} style={{marginBottom:"1rem",border:"1px solid var(--line)",overflow:"hidden"}}>
                    <div onClick={()=>toggleWeek("rm_"+mKey)} style={{background:"var(--ink)",color:"white",padding:".75rem 1.25rem",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",userSelect:"none"}}>
                      <div style={{display:"flex",alignItems:"center",gap:".75rem"}}>
                        <span style={{fontSize:".9rem",display:"inline-block",transform:isMonthOpen?"rotate(90deg)":"rotate(0deg)",transition:"transform .15s"}}>›</span>
                        <span style={{fontSize:".78rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase"}}>{monthLabel}</span>
                        {isCurMonth&&<span style={{fontSize:".6rem",background:"var(--red)",color:"white",padding:".15rem .5rem",letterSpacing:".08em",textTransform:"uppercase"}}>This month</span>}
                      </div>
                      <span style={{fontSize:".72rem",opacity:.8,fontWeight:600}}>{totalInMonth} booking{totalInMonth!==1?"s":""}</span>
                    </div>
                    {isMonthOpen&&Object.entries(weeks).sort(([a],[b])=>a.localeCompare(b)).map(([wk,items])=>{
                      const mon2=new Date(wk+"T12:00:00");
                      const sun2=new Date(mon2);sun2.setDate(mon2.getDate()+6);
                      const wkLabel=`${mon2.toLocaleDateString("en-AU",{day:"numeric",month:"short"})} – ${sun2.toLocaleDateString("en-AU",{day:"numeric",month:"short"})}`;
                      const isCurWk3=wk===curWk3;
                      const isOpen3=openWeeks["room_"+wk]!==undefined?openWeeks["room_"+wk]:isCurWk3;
                      return(
                        <div key={wk} style={{borderTop:"1px solid var(--line)"}}>
                          <div onClick={()=>toggleWeek("room_"+wk)} style={{background:"var(--off)",padding:".6rem 1.25rem .6rem 2rem",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:isOpen3?"1px solid var(--line)":"none",cursor:"pointer",userSelect:"none"}}>
                            <div style={{display:"flex",alignItems:"center",gap:".75rem"}}>
                              <span style={{fontSize:".8rem",display:"inline-block",transform:isOpen3?"rotate(90deg)":"rotate(0deg)",transition:"transform .15s"}}>›</span>
                              <span style={{fontSize:".68rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"var(--ink)"}}>{wkLabel}</span>
                              {isCurWk3&&<span style={{fontSize:".6rem",background:"var(--red)",color:"white",padding:".15rem .5rem",letterSpacing:".08em",textTransform:"uppercase"}}>This week</span>}
                            </div>
                            <span style={{fontSize:".68rem",color:"var(--red)",fontWeight:600}}>{items.length} booking{items.length!==1?"s":""}</span>
                          </div>
                          {isOpen3&&<table className="member-table" style={{border:"none"}}>
                            <thead><tr><th>Date</th><th>Room</th><th>Time</th><th>Booked by</th><th></th></tr></thead>
                            <tbody>
                              {items.map(b=>{
                                const d=new Date(b.date+"T12:00:00");
                                const label=d.toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"});
                                return(
                                  <tr key={b.id}>
                                    <td>{label}</td>
                                    <td>{ROOMS.find(r=>r.id===b.room_id)?.icon} {b.room_name}</td>
                                    <td>{fmtTime(b.start_time)} – {fmtTime(b.end_time)}</td>
                                    <td>{b.name||members[b.email]?.name||b.email}</td>
                                    <td style={{display:"flex",gap:".5rem"}}><button className="btn-remove" style={{color:"#2d7a4f",borderColor:"#2d7a4f"}} onClick={()=>{setEditBookingModal({type:"room",item:b});setEditStartTime(b.start_time);setEditEndTime(b.end_time);setEditDate(b.date);}}>Edit</button><button className="btn-remove" onClick={()=>cancelRoomBooking(b)}>Cancel</button></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}</div>;
            })()}
            {/* Admin: book full day for a member */}
            <div className="recur-form" style={{marginTop:"1.5rem"}}>
              <h4>Book full day for a member</h4>
              <div style={{display:"flex",gap:".75rem",flexWrap:"wrap",alignItems:"flex-end"}}>
                <div className="field" style={{margin:0,flex:2,minWidth:"150px"}}>
                  <label>Member</label>
                  <select className="cap-select" style={{width:"100%"}} value={adminRoomEmail} onChange={e=>setAdminRoomEmail(e.target.value)}>
                    <option value="">Select member…</option>
                    {Object.entries(members).map(([email,m])=><option key={email} value={email}>{m.name}</option>)}
                  </select>
                </div>
                <div className="field" style={{margin:0,flex:1,minWidth:"160px"}}>
                  <label>Room</label>
                  <select className="cap-select" style={{width:"100%"}} value={adminRoomId} onChange={e=>setAdminRoomId(e.target.value)}>
                    {ROOMS.map(r=><option key={r.id} value={r.id}>{r.icon} {r.name}</option>)}
                  </select>
                </div>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"130px"}}>
                  <label>Date</label>
                  <input type="date" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={adminRoomDate} onChange={e=>setAdminRoomDate(e.target.value)}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:".35rem",alignSelf:"flex-end"}}>
                  <button className="btn-add" onClick={async()=>{
                    if(!adminRoomEmail||!adminRoomDate){setAdminRoomMsg("Please select a member and date.");setTimeout(()=>setAdminRoomMsg(""),3000);return;}
                    const room=ROOMS.find(r=>r.id===adminRoomId);
                    const member=members[adminRoomEmail];
                    const booking={id:Date.now().toString(),room_id:room.id,room_name:room.name,date:adminRoomDate,start_time:"08:00",end_time:"20:00",email:adminRoomEmail,name:member?.name||adminRoomEmail};
                    await addRoomBooking(booking);
                    setRoomBookings(r=>[...r,booking]);
                    const d=new Date(adminRoomDate+"T12:00:00");
                    const dateStr=d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
                    const emailHtml=`<p>Hi ${member?.name}, ${room.name} has been booked for you all day on ${dateStr} (8am–8pm).</p>`;
                    sendEmail({to:adminRoomEmail,subject:`Full day room booking – ${room.name} ${adminRoomDate}`,html:emailHtml});
                    sendEmail({to:"studio@yoursonly.com",subject:`Full day room booking – ${room.name} ${adminRoomDate}`,html:emailHtml});
                    setAdminRoomMsg(`✓ Full day booked for ${member?.name}`);
                    setTimeout(()=>setAdminRoomMsg(""),4000);
                  }}>Book full day</button>
                  {adminRoomMsg&&<span style={{fontSize:".75rem",color:adminRoomMsg.startsWith("✓")?"var(--mint-txt)":"var(--red)",fontWeight:600}}>{adminRoomMsg}</span>}
                </div>
              </div>
            </div>

            {/* Recurring room bookings */}
            {recurringRooms.length>0&&(
              <div style={{border:"1px solid var(--line)",marginBottom:"1.5rem",overflow:"hidden"}}>
                <div style={{background:"var(--off)",padding:".6rem 1.25rem",fontSize:".72rem",fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",borderBottom:"1px solid var(--line)"}}>Recurring room bookings</div>
                {recurringRooms.map(rec=>{
                  const daysArr=Array.isArray(rec.days_of_week)?rec.days_of_week:JSON.parse(rec.days_of_week||"[]");
                  const dayLabels=daysArr.map(d=>["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]).join(", ");
                  const roomIcon=ROOMS.find(r=>r.id===rec.room_id)?.icon||"";
                  return(
                    <div key={rec.id} className="recur-list-item">
                      <div>
                        <strong style={{fontSize:".85rem",color:"var(--ink)"}}>{members[rec.email]?.name||rec.email}</strong>
                        <div style={{fontSize:".72rem",color:"#666",marginTop:".2rem"}}>{rec.email}</div>
                      </div>
                      <div style={{flex:1,paddingLeft:"1rem"}}>
                        <span className="recur-badge">{roomIcon} {rec.room_name}</span>
                        <span className="recur-badge">{dayLabels}</span><span className="recur-badge">{rec.cadence&&rec.cadence>1?`Every ${rec.cadence} weeks`:"Every week"}</span>
                        <span className="recur-badge">{fmtTime(rec.start_time)} – {fmtTime(rec.end_time)}</span>
                        {rec.label&&<span className="recur-badge">{rec.label}</span>}
                        <div style={{fontSize:".7rem",color:"#888",marginTop:".3rem"}}>{rec.start_date} → {rec.end_date||"end of year"}</div>
                      </div>
                      <button className="btn-remove" onClick={async()=>{
                        if(!window.confirm(`Remove recurring room booking for ${members[rec.email]?.name||rec.email}? This will cancel all future generated bookings.`)) return;
                        await deleteRecurringRoom(rec.id);
                        setRecurringRooms(rr=>rr.filter(r=>r.id!==rec.id));
                        // Delete all future room bookings matching this rule
                        const todayD=new Date().toISOString().slice(0,10);
                        const toDelete=roomBookings.filter(b=>
                          b.email===rec.email &&
                          b.room_id===rec.room_id &&
                          b.start_time===rec.start_time &&
                          b.end_time===rec.end_time &&
                          b.date>=todayD
                        );
                        for(const b of toDelete){
                          await removeRoomBooking(b.id);
                        }
                        setRoomBookings(rb=>rb.filter(b=>!(
                          b.email===rec.email &&
                          b.room_id===rec.room_id &&
                          b.start_time===rec.start_time &&
                          b.end_time===rec.end_time &&
                          b.date>=todayD
                        )));
                      }}>Remove</button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="recur-form" style={{marginTop:"1.5rem"}}>
              <h4>Add recurring room booking</h4>
              <div style={{display:"flex",gap:".75rem",flexWrap:"wrap",alignItems:"flex-end"}}>
                <div className="field" style={{margin:0,flex:2,minWidth:"150px"}}>
                  <label>Member</label>
                  <select className="cap-select" style={{width:"100%"}} value={recurRoom.email} onChange={e=>setRecurRoom(r=>({...r,email:e.target.value}))}>
                    <option value="">Select member…</option>
                    {Object.entries(members).map(([email,m])=><option key={email} value={email}>{m.name}</option>)}
                  </select>
                </div>
                <div className="field" style={{margin:0,flex:1,minWidth:"160px"}}>
                  <label>Room</label>
                  <select className="cap-select" style={{width:"100%"}} value={recurRoom.room_id} onChange={e=>setRecurRoom(r=>({...r,room_id:e.target.value}))}>
                    {ROOMS.map(r=><option key={r.id} value={r.id}>{r.icon} {r.name}</option>)}
                  </select>
                </div>
                <div className="field" style={{margin:0,minWidth:"120px"}}>
                  <label>Start time</label>
                  <select className="cap-select" style={{width:"100%"}} value={recurRoom.start_time||"09:00"} onChange={e=>setRecurRoom(r=>({...r,start_time:e.target.value}))}>
                    {TIME_SLOTS.slice(0,-1).map(s=><option key={s} value={s}>{fmtTime(s)}</option>)}
                  </select>
                </div>
                <div className="field" style={{margin:0,minWidth:"120px"}}>
                  <label>End time</label>
                  <select className="cap-select" style={{width:"100%"}} value={recurRoom.end_time||"10:00"} onChange={e=>setRecurRoom(r=>({...r,end_time:e.target.value}))}>
                    {TIME_SLOTS.slice(1).map(s=><option key={s} value={s}>{fmtTime(s)}</option>)}
                  </select>
                </div>
              </div>
              <div style={{display:"flex",gap:".75rem",flexWrap:"wrap",alignItems:"flex-end",marginTop:".75rem"}}>
                <div className="field" style={{margin:0}}>
                  <label>Days</label>
                  <div style={{display:"flex",gap:".35rem",flexWrap:"wrap"}}>
                    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((day,i)=>(
                      <label key={i} style={{display:"flex",alignItems:"center",gap:".25rem",fontSize:".78rem",cursor:"pointer",padding:".3rem .5rem",border:"1px solid var(--line)",background:recurRoom.days_of_week.includes(i)?"var(--ink)":"white",color:recurRoom.days_of_week.includes(i)?"white":"var(--ink)",borderRadius:"2px"}}>
                        <input type="checkbox" style={{display:"none"}} checked={recurRoom.days_of_week.includes(i)} onChange={e=>{setRecurRoom(r=>({...r,days_of_week:e.target.checked?[...r.days_of_week,i]:r.days_of_week.filter(d=>d!==i)}));}}/>
                        {day}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="field" style={{margin:0,minWidth:"130px"}}>
                  <label>From</label>
                  <input type="date" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={recurRoom.start_date} onChange={e=>setRecurRoom(r=>({...r,start_date:e.target.value}))}/>
                </div>
                <div className="field" style={{margin:0,minWidth:"130px"}}>
                  <label>Until</label>
                  <input type="date" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={recurRoom.end_date} onChange={e=>setRecurRoom(r=>({...r,end_date:e.target.value}))}/>
                </div>
                <div className="field" style={{margin:0,flex:1,minWidth:"160px"}}>
                  <label>Label / reason</label>
                  <input type="text" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={recurRoom.label} onChange={e=>setRecurRoom(r=>({...r,label:e.target.value}))} placeholder="e.g. Weekly team standup"/>
                </div>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"140px"}}>
                  <label>Cadence</label>
                  <select className="cap-select" style={{width:"100%"}} value={recurRoom.cadence} onChange={e=>setRecurRoom(r=>({...r,cadence:Number(e.target.value)}))}>  <option value={1}>Every week</option><option value={2}>Every 2 weeks</option><option value={3}>Every 3 weeks</option><option value={4}>Every 4 weeks</option></select>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:".35rem",alignSelf:"flex-end"}}>
                  <button className="btn-add" onClick={async()=>{
                    if(!recurRoom.email||!recurRoom.days_of_week.length||!recurRoom.start_date){setRecurRoomMsg("Please fill in member, days and start date.");setTimeout(()=>setRecurRoomMsg(""),3000);return;}
                    const room=ROOMS.find(r=>r.id===recurRoom.room_id);
                    const member=members[recurRoom.email];
                    const startT=recurRoom.start_time||"09:00";
                    const endT=recurRoom.end_time||"10:00";
                    const endDateStr=recurRoom.end_date||`${new Date().getFullYear()}-12-31`;
                    // Save the recurring rule
                    const recurRuleId = Date.now().toString();
                    const recurRule = {id:recurRuleId, email:recurRoom.email, room_id:room.id, room_name:room.name, days_of_week:JSON.stringify(recurRoom.days_of_week), start_time:startT, end_time:endT, start_date:recurRoom.start_date, end_date:endDateStr, label:recurRoom.label||member?.name||recurRoom.email, cadence:recurRoom.cadence||1};
                    await saveRecurringRoom(recurRule);
                    setRecurringRooms(rr=>[...rr, {...recurRule, days_of_week:recurRoom.days_of_week}]);
                    // Generate bookings for each matching day
                    const d=new Date(recurRoom.start_date+"T12:00:00");
                    const endD=new Date(endDateStr+"T12:00:00");
                    let count=0;
                    const roomCadence=recurRoom.cadence||1;
                    const roomDowCounts={};
                    while(d<=endD){
                      const dow=d.getDay();
                      if(recurRoom.days_of_week.includes(dow)){
                        if(!roomDowCounts[dow])roomDowCounts[dow]=0;
                        roomDowCounts[dow]++;
                        if(roomCadence===1||roomDowCounts[dow]%roomCadence===1){
                          const ds=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                          const existing=roomBookings.find(b=>b.room_id===recurRoom.room_id&&b.date===ds&&b.start_time===startT);
                          if(!existing){
                            const booking={id:`${Date.now()}-${count}`,room_id:room.id,room_name:room.name,date:ds,start_time:startT,end_time:endT,email:recurRoom.email,name:recurRoom.label||member?.name||recurRoom.email};
                            await addRoomBooking(booking);
                            setRoomBookings(rb=>[...rb,booking]);
                            count++;
                          }
                        }
                      }
                      d.setDate(d.getDate()+1);
                    }
                    // Send email to member
                    const daysText=recurRoom.days_of_week.map(d=>["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]).join(", ");
                    const emailHtml=`<p>Hi ${member?.name}, a recurring ${room.name} booking has been set up for you every ${daysText} from ${fmtTime(startT)} to ${fmtTime(endT)}, starting ${recurRoom.start_date}${recurRoom.end_date?` until ${recurRoom.end_date}`:""}.</p>`;
                    sendEmail({to:recurRoom.email,subject:`Recurring room booking – ${room.name}`,html:emailHtml});
                    sendEmail({to:"studio@yoursonly.com",subject:`Recurring room booking set up – ${room.name} ${daysText}`,html:emailHtml});
                    setRecurRoom({email:"",room_id:"breakout",days_of_week:[],start_date:"",end_date:"",label:"",start_time:"09:00",end_time:"10:00",cadence:1});
                    setRecurRoomMsg(`✓ ${count} bookings created for ${member?.name}`);
                    setTimeout(()=>setRecurRoomMsg(""),4000);
                  }}>Create recurring room booking</button>
                  {recurRoomMsg&&<span style={{fontSize:".75rem",color:recurRoomMsg.startsWith("✓")?"var(--mint-txt)":"var(--red)",fontWeight:600}}>{recurRoomMsg}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* ── RECURRING BOOKINGS ── */}
          <div className="admin-section">
            <div className="section-title"><span>Recurring bookings</span></div>
            <div style={{fontSize:".78rem",color:"#666",marginBottom:"1rem"}}>Set up automatic weekly bookings for a member. These will be applied immediately.</div>

            {/* Existing recurring bookings */}
            {recurring.length > 0 && (
              <div style={{border:"1px solid var(--line)",marginBottom:"1.5rem",overflow:"hidden"}}>
                {recurring.map(rec => {
                  const daysArr = Array.isArray(rec.days_of_week) ? rec.days_of_week : (()=>{try{return JSON.parse(rec.days_of_week||"[]")}catch{return[]}})();
                  const dayLabels = daysArr.map(d=>DOW_NAMES[d]).join(", ");
                  return (
                    <div key={rec.id} className="recur-list-item">
                      <div>
                        <strong style={{fontSize:".85rem",color:"var(--ink)"}}>{members[rec.email]?.name||rec.email}</strong>
                        <div style={{fontSize:".72rem",color:"#666",marginTop:".2rem"}}>{rec.email}</div>
                      </div>
                      <span className="recur-badge">Desk {rec.desk}</span>
                      <span style={{fontSize:".78rem",color:"var(--ink)"}}>{dayLabels}</span>
                      <span style={{fontSize:".72rem",color:"#888"}}>{rec.start_date} → {rec.end_date}</span>
                      <button className="btn-remove" onClick={()=>removeRecurring(rec)}>Remove</button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* New recurring booking form */}
            <div className="recur-form">
              <h4>Add recurring booking</h4>
              <div style={{display:"flex",gap:".75rem",flexWrap:"wrap",alignItems:"flex-end"}}>
                <div className="field" style={{margin:0,flex:2,minWidth:"160px"}}>
                  <label>Member</label>
                  <select className="cap-select" style={{width:"100%"}} value={newRecur.email} onChange={e=>setNewRecur(r=>({...r,email:e.target.value}))}>
                    <option value="">Select member…</option>
                    {Object.entries(members).map(([email,m])=><option key={email} value={email}>{m.name}</option>)}
                  </select>
                </div>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"100px"}}>
                  <label>Desk</label>
                  <select className="cap-select" value={newRecur.desk} onChange={e=>setNewRecur(r=>({...r,desk:e.target.value}))}>
                    {ALL_DESKS.map(d=><option key={d.id} value={String(d.id)}>Desk {d.id}{d.monitor?" 🖥":""}</option>)}
                  </select>
                </div>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"130px"}}>
                  <label>Start date</label>
                  <input type="date" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={newRecur.start_date} onChange={e=>setNewRecur(r=>({...r,start_date:e.target.value}))}/>
                </div>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"130px"}}>
                  <label>End date</label>
                  <input type="date" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={newRecur.end_date} onChange={e=>setNewRecur(r=>({...r,end_date:e.target.value}))}/>
                </div>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"140px"}}>
                  <label>Cadence</label>
                  <select className="cap-select" style={{width:"100%"}} value={newRecur.cadence} onChange={e=>setNewRecur(r=>({...r,cadence:Number(e.target.value)}))}>  <option value={1}>Every week</option><option value={2}>Every 2 weeks</option><option value={3}>Every 3 weeks</option><option value={4}>Every 4 weeks</option></select>
                </div>
              </div>
              <div style={{marginTop:"1rem"}}>
                <label style={{display:"block",fontSize:".75rem",color:"var(--ink)",marginBottom:".5rem",fontWeight:600}}>Days of week</label>
                <div className="day-check-row">
                  {DOW_NAMES.map((name,i)=>(
                    <label key={i} className="day-check">
                      <input type="checkbox" checked={newRecur.days_of_week.includes(i)} onChange={()=>toggleRecurDay(i)}/>
                      {name}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{marginTop:"1rem",display:"flex",alignItems:"center",gap:"1rem"}}>
                <button className="btn-add" onClick={addRecurring}>Create recurring booking</button>
                {recurMsg&&<span style={{fontSize:".75rem",color:recurMsg.startsWith("✓")?"var(--mint-txt)":"var(--red)",fontWeight:600}}>{recurMsg}</span>}
              </div>
            </div>
          </div>

          {/* ── DESK BLOCKING / ASSIGNMENT ── */}
          <div className="admin-section">
            <div className="section-title"><span>Desk management — block or assign</span></div>
            <div style={{fontSize:".78rem",color:"#666",marginBottom:"1rem"}}>Block a desk (nobody can book it) or assign it to a specific member for a period.</div>

            {/* Existing assignments */}
            {deskAssignments.length > 0 && (
              <div style={{border:"1px solid var(--line)",marginBottom:"1.5rem",overflow:"hidden"}}>
                {deskAssignments.map(a => (
                  <div key={a.id} className="recur-list-item">
                    <strong style={{fontSize:".85rem",color:"var(--ink)"}}>Desk {a.desk_id}</strong>
                    {a.email
                      ? <span className="assign-badge">Assigned → {members[a.email]?.name||a.email}</span>
                      : <span className="blocked-badge">Blocked</span>
                    }
                    <span style={{fontSize:".78rem",color:"var(--ink)"}}>{a.label}</span>
                    <span style={{fontSize:".72rem",color:"#888"}}>{a.start_date} → {a.end_date}</span>
                    <button className="btn-remove" onClick={()=>removeDeskAssignment(a.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            {/* New assignment form */}
            <div className="recur-form">
              <h4>Block or assign a desk</h4>
              <div style={{display:"flex",gap:".75rem",flexWrap:"wrap",alignItems:"flex-end"}}>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"100px"}}>
                  <label>Desk</label>
                  <select className="cap-select" value={newAssign.desk_id} onChange={e=>setNewAssign(a=>({...a,desk_id:e.target.value}))}>
                    {ALL_DESKS.map(d=><option key={d.id} value={String(d.id)}>Desk {d.id}{d.monitor?" 🖥":""}</option>)}
                  </select>
                </div>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"120px"}}>
                  <label>Action</label>
                  <select className="cap-select" value={newAssign.type} onChange={e=>setNewAssign(a=>({...a,type:e.target.value,email:e.target.value==="blocked"?"":a.email}))}>
                    <option value="assign">Assign to member</option>
                    <option value="blocked">Block (nobody)</option>
                  </select>
                </div>
                {newAssign.type === "assign" && (
                  <div className="field" style={{margin:0,flex:2,minWidth:"160px"}}>
                    <label>Assign to</label>
                    <select className="cap-select" style={{width:"100%"}} value={newAssign.email} onChange={e=>setNewAssign(a=>({...a,email:e.target.value}))}>
                      <option value="">Select member…</option>
                      {Object.entries(members).map(([email,m])=><option key={email} value={email}>{m.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="field" style={{margin:0,flex:1,minWidth:"140px"}}>
                  <label>Label / reason</label>
                  <input type="text" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={newAssign.label} onChange={e=>setNewAssign(a=>({...a,label:e.target.value}))} placeholder={newAssign.type==="blocked"?"e.g. Maintenance":"e.g. Permanent desk"}/>
                </div>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"130px"}}>
                  <label>Start</label>
                  <input type="date" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={newAssign.start_date} onChange={e=>setNewAssign(a=>({...a,start_date:e.target.value}))}/>
                </div>
                <div className="field" style={{margin:0,flex:"0 0 auto",minWidth:"130px"}}>
                  <label>End</label>
                  <input type="date" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={newAssign.end_date} onChange={e=>setNewAssign(a=>({...a,end_date:e.target.value}))}/>
                </div>
              </div>
              <div style={{marginTop:"1rem",display:"flex",alignItems:"center",gap:"1rem"}}>
                <button className="btn-add" onClick={addDeskAssignment}>{newAssign.type==="blocked"?"Block desk":"Assign desk"}</button>
                {assignMsg&&<span style={{fontSize:".75rem",color:assignMsg.startsWith("✓")?"var(--mint-txt)":"var(--red)",fontWeight:600}}>{assignMsg}</span>}
              </div>
            </div>
          </div>

          {/* ── MANUAL DESK BOOKING (ADMIN) ── */}
          <div className="admin-section">
            <div className="section-title"><span>Manually add desk booking for member</span></div>
            <div style={{display:"flex",gap:".75rem",flexWrap:"wrap",alignItems:"flex-end"}}>
              <div className="field" style={{margin:0,flex:2,minWidth:"160px"}}>
                <label>Member</label>
                <select className="cap-select" style={{width:"100%"}} value={manualBookEmail} onChange={e=>setManualBookEmail(e.target.value)}>
                  <option value="">Select member…</option>
                  {Object.entries(members).map(([email,m])=><option key={email} value={email}>{m.name} ({email})</option>)}
                </select>
              </div>
              <div className="field" style={{margin:0,minWidth:"100px"}}>
                <label>Desk</label>
                <select className="cap-select" style={{width:"100%"}} value={manualBookDesk} onChange={e=>setManualBookDesk(e.target.value)}>
                  {ALL_DESKS.map(d=><option key={d.id} value={d.id}>Desk {d.id}</option>)}
                </select>
              </div>
              <div className="field" style={{margin:0,minWidth:"140px"}}>
                <label>Date</label>
                <input type="date" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={manualBookDate} onChange={e=>setManualBookDate(e.target.value)}/>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:".35rem",alignSelf:"flex-end"}}>
                <button className="btn-add" onClick={async()=>{
                  if(!manualBookEmail||!manualBookDate){setManualBookMsg("Please select a member and date.");setTimeout(()=>setManualBookMsg(""),3000);return;}
                  // Check if already booked
                  if(bookings[manualBookDate]?.[manualBookEmail]){setManualBookMsg(`${members[manualBookEmail]?.name} already has a booking on ${manualBookDate}.`);setTimeout(()=>setManualBookMsg(""),3000);return;}
                  await addBooking(manualBookDate, manualBookEmail, manualBookDesk);
                  const updated={...bookings,[manualBookDate]:{...(bookings[manualBookDate]||{}),[manualBookEmail]:manualBookDesk}};
                  setBookings(updated);
                  setManualBookMsg(`✓ Booked Desk ${manualBookDesk} for ${members[manualBookEmail]?.name} on ${manualBookDate}`);
                  setTimeout(()=>setManualBookMsg(""),4000);
                }}>Add booking</button>
                {manualBookMsg&&<span style={{fontSize:".75rem",color:manualBookMsg.startsWith("✓")?"var(--mint-txt)":"var(--red)",fontWeight:600}}>{manualBookMsg}</span>}
                <button className="btn-cancel-modal" style={{marginTop:".35rem",fontSize:".72rem"}} onClick={async()=>{await reloadBookings();setManualBookMsg("✓ Bookings refreshed from database");;setTimeout(()=>setManualBookMsg(""),3000);}}>↻ Refresh from DB</button>
              </div>
            </div>
          </div>

          {/* All upcoming bookings — weekly blocks */}
          <div className="admin-section">
            <div className="section-title"><span>All upcoming bookings</span></div>
            {adminStats.upcoming.length===0
              ?<div style={{color:"var(--red)",opacity:.4,fontSize:".85rem"}}>No upcoming bookings yet.</div>
              :(()=>{
                // Group by week
                const weeks = {};
                adminStats.upcoming.forEach(([date,slots])=>{
                  const d = new Date(date+"T12:00:00");
                  const day = d.getDay();
                  const diff = d.getDate() - day + (day===0?-6:1); // Mon of that week
                  const mon = new Date(d.setDate(diff));
                  const weekKey = `${mon.getFullYear()}-${String(mon.getMonth()+1).padStart(2,"0")}-${String(mon.getDate()).padStart(2,"0")}`;
                  if(!weeks[weekKey]) weeks[weekKey]=[];
                  Object.entries(slots).forEach(([email,desk])=>{
                    weeks[weekKey].push({date,email,desk});
                  });
                });
                // Get current week key
                const nowD = new Date();
                const nowDay = nowD.getDay();
                const nowDiff = nowD.getDate() - nowDay + (nowDay===0?-6:1);
                const curMon = new Date(nowD); curMon.setDate(nowDiff);
                const curWeekKey = `${curMon.getFullYear()}-${String(curMon.getMonth()+1).padStart(2,"0")}-${String(curMon.getDate()).padStart(2,"0")}`;

                return Object.entries(weeks).sort(([a],[b])=>a.localeCompare(b)).map(([weekStart,entries])=>{
                  const mon = new Date(weekStart+"T12:00:00");
                  const fri = new Date(mon); fri.setDate(fri.getDate()+6);
                  const weekLabel = `${mon.toLocaleDateString("en-AU",{day:"numeric",month:"short"})} – ${fri.toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"})}`;
                  const isCurrent = weekStart === curWeekKey;
                  const isOpen = openWeeks[weekStart] !== undefined ? openWeeks[weekStart] : isCurrent;
                  return(
                    <div key={weekStart} style={{marginBottom:"1rem",border:"1px solid var(--line)",overflow:"hidden"}}>
                      <div onClick={()=>toggleWeek(weekStart)} style={{background:"var(--off)",padding:".75rem 1.25rem",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:isOpen?"1px solid var(--line)":"none",cursor:"pointer",userSelect:"none"}}>
                        <div style={{display:"flex",alignItems:"center",gap:".75rem"}}>
                          <span style={{fontSize:".9rem",color:"var(--ink)",transition:"transform .15s",display:"inline-block",transform:isOpen?"rotate(90deg)":"rotate(0deg)"}}>›</span>
                          <span style={{fontSize:".72rem",fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:"var(--ink)"}}>{weekLabel}</span>
                          {isCurrent&&<span style={{fontSize:".6rem",background:"var(--red)",color:"white",padding:".15rem .5rem",letterSpacing:".08em",textTransform:"uppercase"}}>This week</span>}
                        </div>
                        <span style={{fontSize:".72rem",color:"var(--red)",fontWeight:600}}>{entries.length} booking{entries.length!==1?"s":""}</span>
                      </div>
                      {isOpen&&<table className="member-table" style={{border:"none"}}>
                        <tbody>
                          {entries.sort((a,b)=>a.date.localeCompare(b.date)).map(({date,email,desk})=>{
                            const d=new Date(date+"T12:00:00");
                            const label=d.toLocaleDateString("en-AU",{weekday:"short",day:"numeric",month:"short"});
                            return(
                              <tr key={`${date}-${email}`}>
                                <td style={{width:"130px"}}>{label}</td>
                                <td>{members[email]?.name||email}</td>
                                <td><span className="upcoming-tag">Desk {desk}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>}
                    </div>
                  );
                });
              })()
            }
          </div>

          {/* Members */}
          <div className="admin-section">
            <div className="section-title"><span>Members</span></div>
            <table className="member-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Days / month</th><th></th></tr></thead>
              <tbody>
                {Object.entries(members).map(([email,m])=>{
                  const mn=`${now.getFullYear()}-${pad(now.getMonth()+1)}`;
                  const used=daysInMonthBooked(bookings,email,mn);
                  const upcomingCount=Object.entries(bookings).filter(([date,slots])=>date>=todayStr&&slots[email]).length;
                  return(
                    <tr key={email}>
                      <td>{m.name}{upcomingCount>0&&<span style={{marginLeft:".5rem",fontSize:".65rem",opacity:.5}}>{upcomingCount} booked ahead</span>}</td>
                      <td style={{opacity:.6,fontSize:".78rem"}}>{email}</td>
                      <td><span className={`role-badge ${m.role}`}>{m.role}</span></td>
                      <td>
                        <select className="cap-select" value={m.cap} onChange={e=>updateCap(email,e.target.value)}>{[4,8,12,16,20].map(c=><option key={c} value={c}>{c} days</option>)}</select>
                        <div style={{fontSize:".68rem",color:"#555",marginTop:".2rem"}}>{used} used · {getExtraDays(email,mn)>0&&<span style={{color:"var(--red)"}}>+{getExtraDays(email,mn)} bonus</span>}</div>
                      </td>
                      <td>{email!==user.email&&m.role!=="admin"&&<button className="btn-remove" onClick={()=>removeMember(email)}>Remove</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Grant extra days */}
          <div className="admin-section">
            <div className="section-title"><span>Grant extra days</span></div>
            <div className="add-member-form">
              <div className="field" style={{flex:2}}>
                <label>Member</label>
                <select className="cap-select" style={{width:"100%"}} value={extraDaysEmail} onChange={e=>setExtraDaysEmail(e.target.value)}>
                  <option value="">Select member…</option>
                  {Object.entries(members).filter(([,m])=>m.role==="member").map(([email,m])=><option key={email} value={email}>{m.name}</option>)}
                </select>
              </div>
              <div className="field" style={{flex:"0 0 auto",minWidth:"110px"}}>
                <label>Extra days</label>
                <select className="cap-select" value={extraDaysCount} onChange={e=>setExtraDaysCount(Number(e.target.value))}>
                  {[1,2,3,4,5].map(n=><option key={n} value={n}>+{n} day{n>1?"s":""}</option>)}
                </select>
              </div>
              <div className="field" style={{flex:"0 0 auto",minWidth:"140px"}}>
                <label>For month</label>
                <input type="month" className="day-input" style={{width:"100%",padding:".35rem .6rem",fontSize:".8rem"}} value={extraDaysMonth} onChange={e=>setExtraDaysMonth(e.target.value)}/>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:".35rem",alignSelf:"flex-end"}}>
                <button className="btn-add" onClick={grantExtraDays}>Grant days</button>
                {extraDaysMsg&&<div style={{fontSize:".72rem",color:"var(--mint-txt)",fontWeight:600}}>{extraDaysMsg}</div>}
              </div>
            </div>
          </div>

          {/* Add member */}
          <div className="admin-section">
            <div className="section-title"><span>Add new member</span></div>
            <div style={{fontSize:".78rem",color:"#666",marginBottom:"1rem"}}>A welcome email with login details will be sent directly to the member.</div>
            <div className="add-member-form">
              <div className="field"><label>Full name</label><input type="text" value={newMember.name} onChange={e=>setNewMember(s=>({...s,name:e.target.value}))} placeholder="Jane Smith"/></div>
              <div className="field"><label>Email</label><input type="email" value={newMember.email} onChange={e=>setNewMember(s=>({...s,email:e.target.value}))} placeholder="jane@company.com"/></div>
              <div className="field" style={{flex:"0 0 auto",minWidth:"130px"}}><label>Days / month</label><select className="cap-select" value={newMember.cap} onChange={e=>setNewMember(s=>({...s,cap:Number(e.target.value)}))}>{[4,8,12,16,20].map(c=><option key={c} value={c}>{c} days</option>)}</select></div>
              <div className="field"><label>Password</label><input type="text" value={newMember.password} onChange={e=>setNewMember(s=>({...s,password:e.target.value}))} placeholder="temp password"/></div>
              <button className="btn-add" onClick={addMember}>Add member + send email</button>
              {addMemberMsg&&<div style={{fontSize:".75rem",color:"var(--mint-txt)",fontWeight:600,marginTop:".5rem"}}>{addMemberMsg}</div>}
            </div>
          </div>
        </>}
      </div>

      {/* EDIT BOOKING MODAL */}
      {editBookingModal&&(()=>{
        const item = editBookingModal.item;
        const isDeskEdit = editBookingModal.type==='desk';
        return(
          <div className="modal-overlay" onClick={()=>setEditBookingModal(null)}>
            <div className="modal" style={{maxWidth:"420px"}} onClick={e=>e.stopPropagation()}>
              <div className="modal-eyebrow">{isDeskEdit?"Edit desk booking":"Edit room booking"}</div>
              <h3>{isDeskEdit?`Desk booking – ${editDate}`:`${item.room_name} – ${editDate}`}</h3>
              <div className="field">
                <label>Date</label>
                <input type="date" value={editDate} onChange={e=>setEditDate(e.target.value)} style={{width:"100%",padding:".5rem .75rem",border:"1px solid var(--red)",fontFamily:"var(--font)",fontSize:"1rem"}}/>
              </div>
              {isDeskEdit&&<div className="field">
                <label>Desk</label>
                <select className="cap-select" style={{width:"100%"}} value={editDesk} onChange={e=>setEditDesk(e.target.value)}>
                  {ALL_DESKS.map(d=><option key={d.id} value={d.id}>Desk {d.id}</option>)}
                </select>
              </div>}
              {!isDeskEdit&&<>
                <div className="field">
                  <label>Start time</label>
                  <select className="cap-select" style={{width:"100%"}} value={editStartTime} onChange={e=>setEditStartTime(e.target.value)}>
                    {TIME_SLOTS.slice(0,-1).map(s=><option key={s} value={s}>{fmtTime(s)}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>End time</label>
                  <select className="cap-select" style={{width:"100%"}} value={editEndTime} onChange={e=>setEditEndTime(e.target.value)}>
                    {TIME_SLOTS.slice(1).map(s=><option key={s} value={s}>{fmtTime(s)}</option>)}
                  </select>
                </div>
              </>}
              <div className="modal-actions">
                <button className="btn-danger" style={{marginRight:"auto"}} onClick={async()=>{
                  if(isDeskEdit){
                    const bookingEmail = item.email || user.email;
                    await removeBooking(item.date, bookingEmail);
                    const updated={...bookings};
                    if(updated[item.date]){const d={...updated[item.date]};delete d[bookingEmail];if(!Object.keys(d).length)delete updated[item.date];else updated[item.date]=d;}
                    setBookings(updated);
                  } else {
                    await removeRoomBooking(item.id);
                    setRoomBookings(r=>r.filter(b=>b.id!==item.id));
                  }
                  setEditBookingModal(null);
                }}>Delete booking</button>
                <button className="btn-cancel-modal" onClick={()=>setEditBookingModal(null)}>Cancel</button>
                <button className="btn-confirm" onClick={async()=>{
                  if(isDeskEdit){
                    // Cancel old, add new
                    const bookingEmail = item.email || user.email;
                    await removeBooking(item.date, bookingEmail);
                    await addBooking(editDate, bookingEmail, editDesk);
                    const updated={...bookings};
                    if(updated[item.date]){const d={...updated[item.date]};delete d[bookingEmail];if(!Object.keys(d).length)delete updated[item.date];else updated[item.date]=d;}
                    if(!updated[editDate])updated[editDate]={};
                    updated[editDate][bookingEmail]=editDesk;
                    setBookings(updated);
                  } else {
                    // Update room booking
                    await removeRoomBooking(item.id);
                    const newB={...item,id:Date.now().toString(),date:editDate,start_time:editStartTime,end_time:editEndTime};
                    await addRoomBooking(newB);
                    setRoomBookings(r=>[...r.filter(b=>b.id!==item.id),newB]);
                  }
                  setEditBookingModal(null);
                }}>Save changes</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ROOM BOOKING MODAL */}
      {roomModal&&(()=>{
        const {room,date,slot} = roomModal;
        const endIdx = TIME_SLOTS.indexOf(slot)+1;
        const endTime = TIME_SLOTS[endIdx]||"20:00";
        const d=new Date(date+"T12:00:00");
        const label=d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long"});
        return(
          <div className="modal-overlay" onClick={()=>{setRoomModal(null);setRoomName("");setRoomDuration(1);}}>
            <div className="modal" style={{maxWidth:"480px"}} onClick={e=>e.stopPropagation()}>
              <div className="modal-eyebrow">Room booking</div>
              <h3>{room.icon} {room.name}</h3>
              <div className="modal-sub">{label}</div>
              <div className="field">
                <label>Your name (for the booking)</label>
                <input type="text" value={roomName} onChange={e=>setRoomName(e.target.value)} placeholder={user.name} autoFocus/>
              </div>
              <div className="field">
                <label>Start time</label>
                <select className="cap-select" style={{width:"100%"}} value={slot} onChange={e=>setRoomModal(m=>({...m,slot:e.target.value}))}>
                  {TIME_SLOTS.slice(0,-1).map(s=>{
                    const taken = isSlotTaken(room.id, date, s) && s!==slot;
                    return <option key={s} value={s} disabled={taken}>{fmtTime(s)}{taken?" (taken)":""}</option>;
                  })}
                </select>
              </div>
              <div className="field">
                <label>Duration</label>
                <select className="cap-select" style={{width:"100%"}} value={roomDuration} onChange={e=>setRoomDuration(Number(e.target.value))}>
                  {[1,2,3,4,6,8,12,16,24].map(n=>{
                    const endIdx = TIME_SLOTS.indexOf(slot)+n;
                    const endT = TIME_SLOTS[endIdx];
                    if(!endT) return null;
                    const hrs = n/2;
                    const label2 = hrs<1?`30 min`:hrs===1?`1 hour`:`${hrs} hours`;
                    return <option key={n} value={n}>{label2} (until {fmtTime(endT)})</option>;
                  }).filter(Boolean)}
                </select>
              </div>
              <div style={{background:"var(--red-lt)",border:"1px solid var(--red-bdr)",padding:".75rem 1rem",fontSize:".82rem",color:"var(--red)",marginBottom:"1rem"}}>
                {fmtTime(slot)} – {fmtTime(TIME_SLOTS[TIME_SLOTS.indexOf(slot)+roomDuration]||"20:00")} · {roomDuration/2<1?"30 min":roomDuration/2===1?"1 hour":`${roomDuration/2} hours`}
              </div>
              <div className="modal-actions">
                <button className="btn-cancel-modal" onClick={()=>{setRoomModal(null);setRoomName("");setRoomDuration(1);}}>Cancel</button>
                <button className="btn-confirm" onClick={confirmRoomBooking}>Confirm booking</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* BOOKING MODAL */}
      {modal&&(()=>{
        const d=new Date(modal.date+"T12:00:00");
        const label=d.toLocaleDateString("en-AU",{weekday:"long",day:"numeric",month:"long"});
        const isMine=userHasDay(modal.date);
        const daySlots=slotsFor(modal.date);
        return(
          <div className="modal-overlay" onClick={()=>setModal(null)}>
            <div className="modal" onClick={e=>e.stopPropagation()}>
              <div className="modal-eyebrow">Desk reservation</div>
              <h3>{label}</h3>
              <div className="modal-sub">{isMine?`You have desk ${daySlots[user.email]} booked. Cancel below to change.`:`${DESK_TOTAL-Object.keys(daySlots).length} of ${DESK_TOTAL} desks free — click to select`}</div>
              <FloorPlan date={modal.date} isMine={isMine} readOnly={false}/>
              {selectedDesk&&!isMine&&<div style={{fontSize:".8rem",color:"var(--red)",marginTop:".6rem",opacity:.75}}>Desk {selectedDesk} selected</div>}
              <div className="modal-actions">
                <button className="btn-cancel-modal" onClick={()=>setModal(null)}>Close</button>
                {isMine
                  ?<button className="btn-danger" onClick={()=>cancelBooking(modal.date)}>Cancel booking</button>
                  :<button className="btn-confirm" disabled={!selectedDesk} onClick={confirmBooking}>Confirm desk {selectedDesk||""}</button>
                }
              </div>
            </div>
          </div>
        );
      })()}
    </div></>
  );
}